import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import LeaveForm, { TYPE_LABEL, DUR_LABEL, ST_LABEL, ST_CHIP } from "@/components/kiyana/LeaveForm";
import ProfileScreen from "@/components/mobile/ProfileScreen";
import { Home, Scissors, FileText, Bell, User, ChevronLeft, ChevronRight, ChevronDown, Search, Shirt, Minus, Plus, Check, Calendar, Package, LogOut, MonitorSmartphone, Loader2, Wallet, ClipboardCheck, Paperclip, AlertTriangle, Fingerprint, Banknote, ArrowDownCircle, ArrowUpCircle, X, FileDown, Camera } from "lucide-react";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const GRAD = "linear-gradient(135deg, #1E40AF 0%, #2563EB 50%, #3B82F6 100%)";
const cardM = "bg-white rounded-2xl shadow-sm border border-slate-100";
const inpM = "w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";
const WIB = "Asia/Jakarta";
const wibDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: WIB }) : "");
const todayLabel = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "short", year: "numeric", timeZone: WIB });

const BADGE = {
  jalan: ["Sedang Dikerjakan", "bg-blue-50 text-blue-700 border border-blue-200"],
  belum: ["Belum Dikerjakan", "bg-red-50 text-red-600 border border-red-200"],
  selesai: ["Selesai", "bg-emerald-50 text-emerald-600 border border-emerald-200"],
  qc: ["Masuk QC", "bg-amber-50 text-amber-600 border border-amber-200"],
};

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function Avatar({ name, size = "w-11 h-11", text = "text-sm" }) {
  return <div className={`${size} rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold ${text} shrink-0`}>{initials(name)}</div>;
}

function ProdThumb({ img, size = "w-14 h-14" }) {
  if (img) return <img src={img} alt="" className={`${size} rounded-xl object-cover border border-slate-100 shrink-0`} />;
  return <div className={`${size} rounded-xl bg-pink-50 text-pink-400 flex items-center justify-center shrink-0`}><Shirt className="w-6 h-6" /></div>;
}

function SubHeader({ title, onBack, right, testid }) {
  return (
    <div className="relative flex items-center gap-2 px-4 pt-4 pb-3 sticky top-0 z-30" style={{ background: "#F8FAFC" }} data-testid={testid}>
      <button data-testid={`${testid}-back`} onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><ChevronLeft className="w-5 h-5" /></button>
      <h1 className="flex-1 text-center font-bold text-slate-900 text-base">{title}</h1>
      {right || <span className="w-9 shrink-0" />}
    </div>
  );
}

export default function MobileProduction() {
  const { user, logout, hasPerm } = useAuth();
  const nav = useNavigate();
  const [screen, setScreen] = useState({ name: "dashboard" });
  const [options, setOptions] = useState({ employees: [] });
  const [bundles, setBundles] = useState([]);
  const [monitor, setMonitor] = useState([]);
  const [events, setEvents] = useState([]);
  const [wagesMe, setWagesMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState(1);
  const [success, setSuccess] = useState(null);
  const [qcPending, setQcPending] = useState([]);
  const [qcSuccess, setQcSuccess] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [myTasks, setMyTasks] = useState({ spk: [], bundles: [] });
  const [batches, setBatches] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const canSew = hasPerm("production.sewing");
  const canQc = hasPerm("production.qc");
  const canCut = hasPerm("production.cutting");

  const load = useCallback(async () => {
    try {
      // Tiap sumber data punya fallback sendiri — role non-produksi (crew/karyawan/host) tanpa izin
      // production.* tetap bisa memakai fitur HR (absen/izin/kasbon/upah) tanpa error.
      const emp = { data: { employees: [], products: [] } };
      const [o, b, m, e, w, qp, rp, mt, bt] = await Promise.all([
        api.get("/prod/options").catch(() => emp),
        api.get("/prod/sewing/bundles").catch(() => ({ data: [] })),
        api.get("/prod/monitoring").catch(() => ({ data: [] })),
        api.get("/prod/events").catch(() => ({ data: [] })),
        api.get("/prod/wages/me").catch(() => ({ data: null })),
        api.get("/prod/qc/pending").catch(() => ({ data: [] })), api.get("/prod/repairs").catch(() => ({ data: [] })),
        api.get("/prod/my/tasks").catch(() => ({ data: { spk: [], bundles: [] } })),
        api.get("/prod/batches").catch(() => ({ data: [] })),
      ]);
      setOptions(o.data); setBundles(b.data); setMonitor(m.data); setEvents(e.data); setWagesMe(w.data); setQcPending(qp.data); setRepairs(rp.data);
      setMyTasks(mt.data); setBatches(bt.data);
    } catch { toast.error("Gagal memuat data produksi"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Segarkan antrean QC setiap kali layar pemeriksaan dibuka (hindari bundel basi yang sudah diperiksa sesi lain)
  useEffect(() => {
    if (screen.name === "qcPick") api.get("/prod/qc/pending").then((r) => setQcPending(r.data)).catch(() => {});
  }, [screen.name]);

  const today = wibDay(new Date().toISOString());
  const receivedToday = useMemo(() => events.filter((e) => e.action === "setoran_jahit" && wibDay(e.created_at) === today).reduce((a, e) => a + (e.qty || 0), 0), [events, today]);
  const inProcess = useMemo(() => bundles.filter((b) => !b.parent_id && b.sewer_id && b.status === "ready_jahit").reduce((a, b) => a + (b.ready_qty || 0), 0), [bundles]);
  const sisaReady = useMemo(() => bundles.filter((b) => !b.parent_id && !b.sewer_id && b.status === "ready_jahit").reduce((a, b) => a + (b.ready_qty || 0), 0), [bundles]);
  const totalPipeline = receivedToday + inProcess + sisaReady;
  const progressPct = totalPipeline > 0 ? Math.round((receivedToday * 100) / totalPipeline) : 0;

  const spkImg = useMemo(() => { const m = {}; monitor.forEach((s) => { m[s.id] = s.image; }); return m; }, [monitor]);
  const myRepairs = useMemo(() => repairs.filter((r) => r.sewer_id === user?.id && ["proses", "disetor"].includes(r.status)), [repairs, user]);
  const myWorking = useMemo(() => bundles.filter((b) => b.sewer_id === user?.id && !b.parent_id && b.status === "ready_jahit" && b.ready_qty > 0), [bundles, user]);
  const myWaitingQc = useMemo(() => bundles.filter((b) => b.sewer_id === user?.id && b.status === "dijahit"), [bundles, user]);
  const mySpks = useMemo(() => myTasks.spk || [], [myTasks]);
  const returnQueue = useMemo(() => (canSew ? repairs.filter((r) => r.status === "proses") : []), [repairs, canSew]);
  const recheckQueue = useMemo(() => (canQc ? repairs.filter((r) => r.status === "disetor") : []), [repairs, canQc]);

  const returnRepair = async (id) => {
    try { await api.post(`/prod/repairs/${id}/submit`); toast.success("Hasil perbaikan disetor — masuk antrean periksa ulang QC"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetor perbaikan"); }
  };
  const sewers = useMemo(() => options.employees.filter((e) => ["penjahit", "mandor"].includes(e.role)), [options]);
  const sewerBundleCount = useMemo(() => {
    const m = {};
    bundles.forEach((b) => { if (!b.parent_id && b.sewer_id && b.status === "ready_jahit" && b.ready_qty > 0) m[b.sewer_id] = (m[b.sewer_id] || 0) + 1; });
    return m;
  }, [bundles]);

  const curSewer = options.employees.find((e) => e.id === screen.sewerId);
  const curBundle = bundles.find((b) => b.id === screen.bundleId);
  const curSisa = curBundle?.ready_qty || 0;
  const bundleChildren = useMemo(() => (curBundle ? bundles.filter((b) => b.parent_id === curBundle.id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")) : []), [bundles, curBundle]);

  const goSewer = (id) => setScreen({ name: "bundles", sewerId: id });
  const goBundle = (id) => { setQty(1); setScreen({ name: "submit", sewerId: screen.sewerId, bundleId: id }); };

  const submit = async () => {
    if (!curBundle) return;
    if (!(qty > 0)) return toast.error("Qty wajib diisi");
    if (qty > curSisa) return toast.error(`Maksimal ${curSisa} pcs (sisa bundle)`);
    setBusy(true);
    try {
      const r = await api.post("/prod/sewing/submit", { sewer_id: curBundle.sewer_id || screen.sewerId, bundle_id: curBundle.id, qty });
      setSuccess({ qty, number: r.data.bundle.number, bundleId: curBundle.id, sewerId: curBundle.sewer_id || screen.sewerId,
                   sewerName: curBundle.sewer_name || curSewer?.name, wage: r.data.wage?.amount || 0 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat hasil jahit"); } finally { setBusy(false); }
  };

  const badgeFor = (b) => {
    if (b.status === "dijahit" || b.parent_id) return BADGE.qc;
    const sewn = (b.qty || 0) - (b.ready_qty || 0);
    return sewn > 0 ? BADGE.jalan : BADGE.belum;
  };

  const NAV = [
    { id: "dashboard", label: "Beranda", icon: Home, testid: "nav-beranda" },
    { id: "produksi", label: "Produksi", icon: Scissors, testid: "nav-produksi" },
    { id: "spk", label: "SPK", icon: FileText, testid: "nav-spk" },
    { id: "notif", label: "Notifikasi", icon: Bell, testid: "nav-notifikasi" },
    { id: "profil", label: "Profil", icon: User, testid: "nav-profil" },
  ];
  const navActive = ["sewers", "bundles", "submit", "detail", "qcPick", "qcForm", "tasks", "recheckForm", "freeBundles", "freeSewer"].includes(screen.name) ? "produksi"
    : ["spkMine", "spkDetail", "cutForm"].includes(screen.name) ? "spk"
    : ["absen", "izin", "kasbon", "upah"].includes(screen.name) ? "dashboard" : screen.name;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-50 relative pb-24" data-testid="mobile-mandor-app">
      {/* ============ DASHBOARD ============ */}
      {screen.name === "dashboard" && (
        <div data-testid="mobile-mandor-dashboard">
          <div className="px-4 pt-5 pb-16 rounded-b-3xl" style={{ background: GRAD }}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-white text-lg tracking-wide">Kiyana</span>
              <button data-testid="dashboard-bell" onClick={() => setScreen({ name: "notif" })} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white relative">
                <Bell className="w-4.5 h-4.5" />
                {receivedToday > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400" />}
              </button>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Avatar name={user?.name} />
              <div className="flex-1">
                <div className="text-blue-100 text-[11px]" data-testid="mandor-role">{user?.role_label || "Karyawan Produksi"}</div>
                <div className="text-white font-bold text-base leading-tight" data-testid="mandor-greeting">{user?.name}</div>
              </div>
              <span className="text-blue-100 text-[11px]">{todayLabel}</span>
            </div>
            <div className={`${cardM} mt-4 p-4 !bg-white/10 !border-white/20`} data-testid="progress-today-card">
              <div className="flex items-center justify-between text-white">
                <span className="text-xs font-semibold">Progres Hari Ini</span>
                <span className="text-xs font-bold">{receivedToday.toLocaleString("id-ID")} / {totalPipeline.toLocaleString("id-ID")} pcs</span>
              </div>
              <div className="h-2 rounded-full bg-white/20 mt-2.5 overflow-hidden">
                <div className="h-full rounded-full bg-white transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="text-right text-[11px] text-blue-100 mt-1 font-semibold">{progressPct}%</div>
            </div>
          </div>
          <div className="px-4 -mt-8 grid grid-cols-3 gap-2.5">
            {[
              { label: "Sudah Diterima", value: receivedToday, cls: "text-blue-600", tid: "stat-diterima-count" },
              { label: "Dalam Proses", value: inProcess, cls: "text-amber-500", tid: "stat-proses-count" },
              { label: "Sisa", value: sisaReady, cls: "text-red-500", tid: "stat-sisa-count" },
            ].map((s) => (
              <div key={s.label} className={`${cardM} p-3 text-center`}>
                <div className={`text-2xl font-bold ${s.cls}`} data-testid={s.tid}>{s.value.toLocaleString("id-ID")}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="px-4 mt-3" data-testid="saldo-card">
            <div className="rounded-2xl p-4 text-white shadow-lg shadow-blue-200" style={{ background: GRAD }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-blue-100">Saldo Aktif Saya</div>
                  <div className="text-2xl font-bold mt-0.5" data-testid="saldo-aktif">{formatIDR(wagesMe?.saldo ?? wagesMe?.total?.layak_bayar ?? 0)}</div>
                </div>
                <span className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
              </div>
              <div className="flex gap-4 mt-2.5 text-[10px] text-blue-100">
                <span>Ditahan QC: <b data-testid="saldo-ditahan">{formatIDR(wagesMe?.total?.ditahan || 0)}</b></span>
                <span>Sudah Dibayar: <b data-testid="saldo-terbayar">{formatIDR(wagesMe?.total?.terbayar || 0)}</b></span>
                {(wagesMe?.kasbon_sisa || 0) > 0 && <span>Kasbon: <b data-testid="saldo-kasbon">{formatIDR(wagesMe.kasbon_sisa)}</b></span>}
              </div>
            </div>
          </div>
          <div className="px-4 mt-5 grid grid-cols-4 gap-2.5">
            {[
              ...(canSew ? [
                { label: "Input Hasil Jahit", icon: Scissors, tid: "menu-input-hasil-jahit", fn: () => setScreen({ name: "sewers" }) },
                { label: "Pembagian Jahit", icon: Package, tid: "menu-pembagian-jahit", fn: () => setAssignOpen(true) },
              ] : []),
              ...(canQc ? [{ label: "Input Hasil QC", icon: ClipboardCheck, tid: "menu-input-hasil-qc", fn: () => setScreen({ name: "qcPick" }) }] : []),
              ...(canCut ? [{ label: "Input Hasil Potong", icon: Scissors, tid: "menu-input-hasil-potong", fn: () => setScreen({ name: "spkMine" }) }] : []),
              { label: "Absen", icon: Fingerprint, tid: "menu-absen", fn: () => setScreen({ name: "absen" }) },
              { label: "Izin / Cuti", icon: FileText, tid: "menu-izin", fn: () => setScreen({ name: "izin" }) },
              { label: "Kasbon", icon: Banknote, tid: "menu-kasbon", fn: () => setScreen({ name: "kasbon" }) },
              { label: "Upah", icon: Wallet, tid: "menu-upah", fn: () => setScreen({ name: "upah" }) },
              { label: "Riwayat", icon: FileText, tid: "menu-riwayat", fn: () => setScreen({ name: "notif" }) },
              { label: "Laporan", icon: FileText, tid: "menu-laporan", fn: () => toast.info("Laporan sementara lewat versi desktop") },
            ].map((m) => (
              <button key={m.tid} data-testid={m.tid} onClick={m.fn} className="flex flex-col items-center gap-1.5">
                <span className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100"><m.icon className="w-5 h-5" /></span>
                <span className="text-[10px] text-slate-500 text-center leading-tight">{m.label}</span>
              </button>
            ))}
          </div>
          <div className="px-4 mt-6">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="font-bold text-slate-900 text-sm">Daftar Pekerjaan Saya</h2>
              <button data-testid="work-lihat-semua" onClick={() => setScreen({ name: "tasks" })} className="text-[11px] text-blue-600 font-semibold flex items-center">Lihat Semua <ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
            <WorkList
              repairs={myRepairs.slice(0, 3)} working={myWorking.slice(0, 3)} waiting={myWaitingQc.slice(0, 2)}
              qcQueue={(canQc && !canSew ? qcPending : []).slice(0, 3)}
              spkQueue={(canCut ? mySpks : []).slice(0, 3)}
              returnQueue={returnQueue.slice(0, 3)} recheckQueue={recheckQueue.slice(0, 3)} employees={options.employees}
              onPickBundle={(id) => { setQty(1); setScreen({ name: "submit", sewerId: user?.id, bundleId: id }); }}
              onPickQc={(id) => setScreen({ name: "qcForm", bundleId: id })}
              onPickSpk={(id) => setScreen({ name: "spkDetail", spkId: id })}
              onReturn={returnRepair} onRecheck={(id) => setScreen({ name: "recheckForm", repairId: id })} />
          </div>
        </div>
      )}

      {/* ============ SPK LIST ============ */}
      {screen.name === "spk" && (
        <div data-testid="screen-spk">
          <SubHeader title="SPK Produksi" onBack={() => setScreen({ name: "dashboard" })} testid="spk-header" />
          <div className="px-4 space-y-2.5 pb-4">
            {monitor.map((s) => <SpkCard key={s.id} s={s} />)}
            {!monitor.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada SPK.</div>}
          </div>
        </div>
      )}

      {/* ============ PILIH PENJAHIT ============ */}
      {screen.name === "sewers" && <SewerPicker sewers={sewers} counts={sewerBundleCount} onBack={() => setScreen({ name: "dashboard" })} onPick={goSewer} onFree={() => setScreen({ name: "freeBundles" })} />}

      {/* ============ BAGIKAN BUNDEL (modal, seperti backend desktop) ============ */}
      {assignOpen && canSew && (
        <AssignBundleModal bundles={bundles} employees={options.employees} busy={busy} setBusy={setBusy}
          onClose={() => setAssignOpen(false)} onSaved={async () => { setAssignOpen(false); await load(); }} />
      )}

      {/* ============ BUNDEL LUPA DIBAGIKAN (ready, belum dikerjakan siapa pun) ============ */}
      {screen.name === "freeBundles" && (
        <div data-testid="screen-free-bundles">
          <SubHeader title="Bundel Belum Dibagikan" onBack={() => setScreen({ name: "sewers" })} testid="freeb-header" />
          <FreeBundlePicker bundles={bundles} onPick={(id) => setScreen({ name: "freeSewer", bundleId: id })} />
        </div>
      )}
      {screen.name === "freeSewer" && (
        <SewerPicker sewers={sewers} counts={sewerBundleCount} onBack={() => setScreen({ name: "freeBundles" })}
          onPick={(id) => { setQty(bundles.find((b) => b.id === screen.bundleId)?.qty || 1); setScreen({ name: "submit", sewerId: id, bundleId: screen.bundleId }); }} />
      )}

      {/* ============ INPUT HASIL QC (role Finishing & QC) ============ */}
      {screen.name === "qcPick" && (
        <QcPickScreen pending={qcPending} employees={options.employees} onBack={() => setScreen({ name: "dashboard" })}
          onPick={(id) => setScreen({ name: "qcForm", bundleId: id })} />
      )}
      {screen.name === "qcForm" && (() => {
        const qb = qcPending.find((x) => x.id === screen.bundleId);
        return qb ? (
          <QcFormScreen bundle={qb} onBack={() => setScreen({ name: "qcPick" })}
            onDone={async (res) => { await load(); setQcSuccess(res); }}
            onStale={async () => { await load(); setScreen({ name: "qcPick" }); }} />
        ) : null;
      })()}

      {/* ============ SPK SAYA (role cutting — hanya SPK yang ditugaskan ke akun ini) ============ */}
      {screen.name === "spkMine" && (
        <div data-testid="screen-spk-mine">
          <SubHeader title="SPK Saya" onBack={() => setScreen({ name: "dashboard" })} testid="spkmine-header" />
          <div className="px-4 pb-4 space-y-2.5">
            <p className="text-[11px] text-slate-400 leading-snug">Hanya SPK yang ditugaskan ke Anda yang tampil di sini.</p>
            {mySpks.map((s) => <SpkMineCard key={s.id} s={s} batches={batches} onPick={(id) => setScreen({ name: "spkDetail", spkId: id })} />)}
            {!mySpks.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="spkmine-empty">Belum ada SPK yang ditugaskan ke Anda.</div>}
          </div>
        </div>
      )}
      {screen.name === "spkDetail" && (() => {
        const s = mySpks.find((x) => x.id === screen.spkId);
        return s ? <SpkDetailScreen spk={s} batches={batches} materials={options.materials || []} onBack={() => setScreen({ name: "spkMine" })} onCut={(variant) => setScreen({ name: "cutForm", spkId: s.id, variant })} /> : null;
      })()}
      {screen.name === "cutForm" && (() => {
        const s = mySpks.find((x) => x.id === screen.spkId);
        return s ? (
          <CutFormScreen spk={s} variant={screen.variant} onBack={() => setScreen({ name: "spkDetail", spkId: s.id })}
            onDone={async (msg) => { await load(); setScreen({ name: "spkDetail", spkId: s.id }); toast.success(msg); }} />
        ) : null;
      })()}

      {/* ============ BUNDLE PENJAHIT ============ */}
      {screen.name === "bundles" && curSewer && (
        <div data-testid="screen-bundles">
          <SubHeader title="Bundle Penjahit" onBack={() => setScreen({ name: "sewers" })} testid="bundles-header" />
          <div className="px-4">
            <div className={`${cardM} p-3.5 flex items-center gap-3`}>
              <Avatar name={curSewer.name} />
              <div className="flex-1">
                <div className="font-bold text-slate-900 text-sm">{curSewer.name}</div>
                <div className="text-[11px] text-slate-400">ID: {curSewer.employee_id || "-"}</div>
              </div>
              <button data-testid="ganti-penjahit" onClick={() => setScreen({ name: "sewers" })} className="h-8 px-3 rounded-lg border border-blue-200 text-blue-600 text-[11px] font-semibold">Ganti Penjahit</button>
            </div>
          </div>
          <BundleList bundles={bundles.filter((b) => b.sewer_id === curSewer.id && !b.parent_id && b.status === "ready_jahit")} spkImg={spkImg} badgeFor={badgeFor} onPick={goBundle} empty="Penjahit ini belum memegang bundle — bagikan dulu lewat versi desktop." />
        </div>
      )}

      {/* ============ INPUT HASIL JAHIT ============ */}
      {screen.name === "submit" && curBundle && (
        <div data-testid="screen-submit">
          <SubHeader title="Input Hasil Jahit" onBack={() => (curBundle.sewer_id ? setScreen({ name: "bundles", sewerId: curBundle.sewer_id }) : setScreen({ name: "freeBundles" }))} testid="submit-header" />
          <div className="px-4 space-y-3.5 pb-6">
            <BundleCard b={curBundle} img={spkImg[curBundle.spk_id]} badgeFor={badgeFor} testid="submit-bundle-card" />
            {!curBundle.sewer_id && curSewer && (
              <div data-testid="submit-sewer-chip" className="text-[11px] bg-emerald-50 border border-emerald-100 rounded-xl px-3.5 py-2.5 text-emerald-700">
                Bundel ini belum dibagikan — setoran otomatis tercatat untuk <b>{curSewer.name}</b> ({curSewer.employee_id || "-"}).
              </div>
            )}
            <div className={`${cardM} p-4`}>
              <label className="text-xs font-semibold text-slate-500">Jumlah Diterima Hari Ini</label>
              <div className="flex items-center gap-2.5 mt-2">
                <button data-testid="qty-stepper-decrement" onClick={() => setQty((v) => Math.max(1, v - 1))} className="w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600"><Minus className="w-4 h-4" /></button>
                <div className="flex-1 relative">
                  <input data-testid="qty-stepper-input" type="number" min="1" max={curSisa} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(curSisa, parseInt(e.target.value) || 1)))} className={`${inpM} text-center font-bold text-lg pr-10`} />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">pcs</span>
                </div>
                <button data-testid="qty-stepper-increment" onClick={() => setQty((v) => Math.min(curSisa, v + 1))} className="w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600"><Plus className="w-4 h-4" /></button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2" data-testid="qty-max-note">Maksimal {curSisa} pcs (sisa bundle)</p>
            </div>
            <div className={`${cardM} p-4`}>
              <label className="text-xs font-semibold text-slate-500">Tanggal Terima</label>
              <div className={`${inpM} mt-2 flex items-center gap-2.5 text-slate-700`} data-testid="tanggal-terima">
                <Calendar className="w-4 h-4 text-blue-500" /> {formatDateID(new Date().toISOString())}
              </div>
            </div>
            {canSew ? (
              <button data-testid="submit-result-button" onClick={submit} disabled={busy}
                className="w-full h-12 rounded-2xl text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-blue-200" style={{ background: BLUE }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Terima Hasil Jahit
              </button>
            ) : (
              <p className="text-center text-[11px] text-slate-400" data-testid="submit-noperm">Akun Anda tidak memiliki izin input hasil jahit.</p>
            )}
          </div>
        </div>
      )}

      {/* ============ DETAIL BUNDLE ============ */}
      {screen.name === "detail" && curBundle && (
        <div data-testid="screen-detail">
          <SubHeader title="Detail Bundle" onBack={() => setScreen({ name: "bundles", sewerId: curBundle.sewer_id || screen.sewerId })} testid="detail-header" />
          <div className="px-4 space-y-3.5 pb-6">
            <BundleCard b={curBundle} img={spkImg[curBundle.spk_id]} badgeFor={badgeFor} testid="detail-bundle-card" />
            <div className={`${cardM} p-4`}>
              <h3 className="font-bold text-slate-900 text-sm mb-2.5">Riwayat Penerimaan Jahit</h3>
              <div className="space-y-2" data-testid="detail-history">
                {bundleChildren.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-xs border-b border-slate-50 pb-2" data-testid={`detail-hist-${c.id}`}>
                    <div>
                      <div className="text-slate-600 font-medium">{formatDateID(c.created_at)}</div>
                      <div className="text-[10px] text-slate-400">oleh {c.sewing?.by || "-"}</div>
                    </div>
                    <span className="font-bold text-slate-800">{c.qty} pcs</span>
                  </div>
                ))}
                {curBundle.sewing && !bundleChildren.length && (
                  <div className="flex items-center justify-between text-xs">
                    <div>
                      <div className="text-slate-600 font-medium">{formatDateID(curBundle.sewing.at)}</div>
                      <div className="text-[10px] text-slate-400">oleh {curBundle.sewing.by || "-"}</div>
                    </div>
                    <span className="font-bold text-slate-800">{curBundle.qty} pcs</span>
                  </div>
                )}
                {!bundleChildren.length && !curBundle.sewing && <p className="text-center text-[11px] text-slate-400 py-3">Belum ada setoran untuk bundle ini.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ NOTIFIKASI / RIWAYAT ============ */}
      {screen.name === "notif" && (
        <div data-testid="screen-notif">
          <SubHeader title="Riwayat Produksi" onBack={() => setScreen({ name: "dashboard" })} testid="notif-header" />
          <div className="px-4 space-y-2 pb-4">
            {events.slice(0, 40).map((e) => (
              <div key={e.id} className={`${cardM} p-3.5 flex gap-3`} data-testid={`notif-${e.id}`}>
                <span className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Scissors className="w-4 h-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 capitalize">{String(e.action || "").replace(/_/g, " ")}</div>
                  <div className="text-[11px] text-slate-500 truncate">{e.detail}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{formatDateID(e.created_at)} • {e.user_name}</div>
                </div>
                {e.qty ? <span className="text-xs font-bold text-blue-600 shrink-0">{e.qty} pcs</span> : null}
              </div>
            ))}
            {!events.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada aktivitas.</div>}
          </div>
        </div>
      )}

      {/* ============ PROFIL ============ */}
      {screen.name === "profil" && (
        <ProfileScreen onBack={() => setScreen({ name: "dashboard" })} extra={(
          <>
            {user?.role !== "penjahit" && (
              <button data-testid="profil-desktop" onClick={() => nav("/admin/production")} className={`${cardM} w-full p-4 flex items-center gap-3 text-left`}>
                <span className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center"><MonitorSmartphone className="w-5 h-5" /></span>
                <div className="flex-1"><div className="text-sm font-semibold text-slate-800">Versi Desktop</div><div className="text-[11px] text-slate-400">Buka modul produksi lengkap</div></div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </button>
            )}
            <button data-testid="profil-logout" onClick={async () => { await logout(); nav("/login"); }} className={`${cardM} w-full p-4 flex items-center gap-3 text-left`}>
              <span className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center"><LogOut className="w-5 h-5" /></span>
              <div className="text-sm font-semibold text-red-500">Keluar</div>
            </button>
          </>
        )} />
      )}

      {/* ============ ABSEN / IZIN / KASBON / UPAH (gabungan portal karyawan) ============ */}
      {screen.name === "absen" && <AbsenScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "izin" && <IzinScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "kasbon" && <KasbonScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "upah" && <UpahScreen onBack={() => setScreen({ name: "dashboard" })} user={user} hasPerm={hasPerm} />}

      {/* ============ DAFTAR PEKERJAAN SAYA (full) ============ */}
      {screen.name === "tasks" && (
        <div data-testid="screen-tasks">
          <SubHeader title="Daftar Pekerjaan Saya" onBack={() => setScreen({ name: "dashboard" })} testid="tasks-header" />
          <div className="px-4 pb-4">
            <WorkList repairs={myRepairs} working={myWorking} waiting={myWaitingQc} qcQueue={canQc && !canSew ? qcPending : []}
              returnQueue={returnQueue} recheckQueue={recheckQueue} spkQueue={canCut ? mySpks : []} employees={options.employees}
              onPickBundle={(id) => { setQty(1); setScreen({ name: "submit", sewerId: user?.id, bundleId: id }); }}
              onPickQc={(id) => setScreen({ name: "qcForm", bundleId: id })}
              onPickSpk={(id) => setScreen({ name: "spkDetail", spkId: id })}
              onReturn={returnRepair} onRecheck={(id) => setScreen({ name: "recheckForm", repairId: id })} />
          </div>
        </div>
      )}

      {screen.name === "recheckForm" && (() => {
        const rep = repairs.find((x) => x.id === screen.repairId);
        return rep ? (
          <RecheckFormScreen repair={rep} employees={options.employees} onBack={() => setScreen({ name: "dashboard" })}
            onDone={async (passed) => { await load(); setScreen({ name: "dashboard" }); toast.success(`Periksa ulang tersimpan — ${passed} pcs lolos, saldo yang ditahan kembali ke penjahit`); }} />
        ) : null;
      })()}

      {/* ============ MODAL SUKSES ============ */}
      {success && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6" data-testid="success-modal-dialog">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto flex items-center justify-center shadow-lg shadow-emerald-200">
              <Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
            <h3 className="font-bold text-slate-900 text-lg mt-4">Berhasil Disimpan</h3>
            <p className="text-xs text-slate-500 mt-1.5">Hasil jahit {success.qty} pcs untuk Bundle {success.number} telah dicatat.</p>
            {success.wage > 0 && <p className="text-[11px] text-emerald-600 font-semibold mt-1" data-testid="success-wage-note">Upah {formatIDR(success.wage)} langsung masuk Saldo Aktif {success.sewerName}.</p>}
            <button data-testid="view-bundle-detail-btn" onClick={() => { const s = success; setSuccess(null); setScreen({ name: "detail", bundleId: s.bundleId, sewerId: s.sewerId }); }}
              className="w-full h-11 rounded-xl text-white text-sm font-bold mt-5" style={{ background: BLUE }}>Lihat Detail Bundle</button>
            <button data-testid="input-other-sewer-btn" onClick={() => { setSuccess(null); setScreen({ name: "sewers" }); }}
              className="w-full h-11 rounded-xl border-2 text-sm font-bold mt-2.5" style={{ borderColor: BLUE, color: BLUE }}>Input Penjahit Lain</button>
          </div>
        </div>
      )}

      {/* ============ MODAL SUKSES QC ============ */}
      {qcSuccess && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6" data-testid="qc-success-modal">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto flex items-center justify-center shadow-lg shadow-emerald-200">
              <Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
            <h3 className="font-bold text-slate-900 text-lg mt-4">Pemeriksaan Tersimpan</h3>
            <p className="text-xs text-slate-500 mt-1.5">Bundle {qcSuccess.number}: {qcSuccess.passed} pcs lolos{qcSuccess.repair ? `, ${qcSuccess.repair} perlu perbaikan` : ""}{qcSuccess.reject ? `, ${qcSuccess.reject} reject permanen` : ""}.</p>
            {qcSuccess.passed > 0 && <p className="text-[11px] text-emerald-600 font-semibold mt-1" data-testid="qc-success-wage-note">Upah finishing untuk {qcSuccess.passed} pcs lolos langsung masuk Saldo Aktif Anda.</p>}
            {qcSuccess.repair > 0 && <p className="text-[11px] text-amber-600 mt-1">Bagian perbaikan dikembalikan ke penjahit yang sama — saldo penjahit untuk bagian itu ditahan dulu.</p>}
            <button data-testid="qc-success-next-btn" onClick={() => { setQcSuccess(null); setScreen({ name: "qcPick" }); }}
              className="w-full h-11 rounded-xl text-white text-sm font-bold mt-5" style={{ background: BLUE }}>Periksa Bundel Lain</button>
            <button data-testid="qc-success-home-btn" onClick={() => { setQcSuccess(null); setScreen({ name: "dashboard" }); }}
              className="w-full h-11 rounded-xl border-2 text-sm font-bold mt-2.5" style={{ borderColor: BLUE, color: BLUE }}>Kembali ke Beranda</button>
          </div>
        </div>
      )}

      {/* ============ BOTTOM NAV ============ */}
      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t border-slate-200 z-40 flex justify-around py-2 px-3 shadow-lg rounded-t-2xl" data-testid="bottom-nav">
        {NAV.map((n) => (
          <button key={n.id} data-testid={n.testid} onClick={() => setScreen({ name: n.id === "produksi" ? (canQc && !canSew ? "qcPick" : canCut && !canSew ? "spkMine" : "sewers") : n.id === "spk" && canCut && !canSew && !canQc ? "spkMine" : n.id })}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 min-w-[52px] ${navActive === n.id ? "text-blue-600" : "text-slate-400"}`}>
            <n.icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{n.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SpkCard({ s, compact }) {
  const pct = s.stages?.pct?.jahit || 0;
  const done = (s.stages?.pct?.gudang || 0) >= 100;
  return (
    <div className={`${cardM} p-3.5 flex gap-3`} data-testid={`spk-active-item-${s.id}`}>
      <ProdThumb img={s.image} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-bold text-slate-900 text-xs truncate">{s.product_name}</div>
            <div className="text-[10px] text-slate-400 font-mono">{s.number}</div>
          </div>
          <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${done ? BADGE.selesai[1] : BADGE.jalan[1]}`}>{done ? "Selesai" : "Sedang Dikerjakan"}</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 mt-2.5 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: BLUE }} />
        </div>
        {!compact && <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>Jahit {pct}%</span><span>Target {s.target_qty?.toLocaleString("id-ID")} pcs</span></div>}
        {compact && <div className="text-right text-[10px] text-slate-400 mt-1">{(s.stages?.setoran || 0).toLocaleString("id-ID")} / {(s.target_qty || 0).toLocaleString("id-ID")} pcs</div>}
      </div>
    </div>
  );
}

const SPK_ST = {
  baru: ["Baru", "bg-slate-100 text-slate-500 border border-slate-200"],
  diserahkan: ["Bahan Diserahkan", "bg-blue-50 text-blue-600 border border-blue-200"],
  proses_potong: ["Proses Potong", "bg-amber-50 text-amber-600 border border-amber-200"],
  selesai_potong: ["Selesai Potong", "bg-emerald-50 text-emerald-600 border border-emerald-200"],
};

function SpkMineCard({ s, batches, onPick }) {
  const [lb, cls] = SPK_ST[s.status] || [s.status, "bg-slate-100 text-slate-500 border border-slate-200"];
  const target = (s.variants || []).reduce((a, v) => a + (v.target_qty || 0), 0);
  const gen = new Set(batches.filter((b) => b.spk_id === s.id && b.status !== "draft").map((b) => b.variant)).size;
  return (
    <button data-testid={`spk-card-${s.id}`} onClick={() => onPick(s.id)} className={`${cardM} w-full p-3.5 text-left`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-bold text-slate-700">{s.number}</span>
        <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${cls}`}>{lb}</span>
      </div>
      <div className="text-xs font-semibold text-slate-700 mt-1 truncate">{s.product_name}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">
        Target {target.toLocaleString("id-ID")} pcs • {gen}/{(s.variants || []).length} variasi digenerate{s.deadline ? ` • deadline ${formatDateID(s.deadline)}` : ""}
      </div>
    </button>
  );
}

function SpkDetailScreen({ spk, batches, materials = [], onBack, onCut }) {
  const [lb, cls] = SPK_ST[spk.status] || [spk.status, "bg-slate-100 text-slate-500 border border-slate-200"];
  const processable = ["diserahkan", "proses_potong"].includes(spk.status);
  const batchOf = (variant) => batches.find((b) => b.spk_id === spk.id && b.variant === variant && b.status !== "draft");
  const matName = (m) => m.material_name || materials.find((x) => x.id === m.material_id)?.name || m.material_id;
  return (
    <div data-testid="screen-spk-detail">
      <SubHeader title={`SPK ${spk.number}`} onBack={onBack} testid="spkd-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <div className={`${cardM} p-4`} data-testid="spkd-info">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs font-bold text-slate-800">{spk.number}</span>
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{lb}</span>
          </div>
          <div className="text-sm font-bold text-slate-900 mt-1">{spk.product_name}</div>
          <div className="text-[11px] text-slate-400 mt-1 space-y-0.5">
            {spk.assigned_to_name && <div>Ditugaskan ke: <b className="text-slate-600">{spk.assigned_to_name}</b></div>}
            {spk.location ? <div>Lokasi: {spk.location}</div> : null}
            {spk.deadline ? <div>Deadline: {formatDateID(spk.deadline)}</div> : null}
          </div>
          {spk.instructions ? <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-2.5 mt-2.5">Instruksi: {spk.instructions}</div> : null}
        </div>
        {(spk.materials || []).length > 0 && (
          <div className={`${cardM} p-4`} data-testid="spkd-materials">
            <div className="text-[11px] font-semibold text-slate-500 mb-2">Bahan</div>
            <div className="space-y-1.5">
              {spk.materials.map((m, i) => (
                <div key={i} className="flex justify-between text-xs text-slate-600">
                  <span>{matName(m)}{m.color_lot ? ` • ${m.color_lot}` : ""}</span>
                  <span className="font-semibold">{m.qty}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={`${cardM} p-4 space-y-2.5`} data-testid="spkd-variants">
          <div className="text-[11px] font-semibold text-slate-500">Variasi & Hasil Potong</div>
          {(spk.variants || []).map((v) => {
            const bt = batchOf(v.variant);
            return (
              <div key={v.variant} className="border border-slate-100 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-bold text-slate-800">{v.variant}</div>
                    <div className="text-[10px] text-slate-400">Target {v.target_qty} pcs{bt ? ` • Hasil ${bt.total_qty} pcs • ${bt.bundle_count} bundel (${bt.number})` : ""}</div>
                  </div>
                  {bt ? (
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 font-semibold whitespace-nowrap" data-testid={`spkd-done-${v.variant}`}>Sudah Digenerate</span>
                  ) : processable ? (
                    <button data-testid={`spkd-cut-${v.variant}`} onClick={() => onCut(v.variant)}
                      className="h-8 px-3 rounded-lg text-white text-[11px] font-bold whitespace-nowrap" style={{ background: BLUE }}>Input Hasil Potong</button>
                  ) : (
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 font-semibold whitespace-nowrap">Menunggu bahan diserahkan</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CutFormScreen({ spk, variant, onBack, onDone }) {
  const vrow = (spk.variants || []).find((v) => v.variant === variant) || {};
  const [total, setTotal] = useState(String(vrow.target_qty || ""));
  const [rows, setRows] = useState([""]);
  const [busy, setBusy] = useState(false);
  const t = parseInt(total) || 0;
  const rws = rows.map((r) => parseInt(r) || 0);
  const sum = rws.reduce((a, b) => a + b, 0);
  const balance = t - sum;
  const rowsValid = rows.length > 0 && rws.every((q) => q > 0);
  const save = async (generate) => {
    if (!(t > 0)) return toast.error("Total hasil potong wajib lebih dari 0");
    if (!rowsValid) return toast.error("Isi qty tiap bundel dengan angka lebih dari 0");
    if (generate && balance !== 0) return toast.error(`Balance harus 0 untuk generate (saat ini ${balance} pcs)`);
    setBusy(true);
    try {
      const r = await api.post("/prod/batches", { spk_id: spk.id, variant, total_qty: t, bundle_qtys: rws, generate });
      onDone(generate ? `Batch ${r.data.number} digenerate — ${r.data.bundles.length} bundel siap dibagikan ke penjahit` : "Draft hasil potong tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan hasil potong"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="screen-cut-form">
      <SubHeader title="Input Hasil Potong" onBack={onBack} testid="cut-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <div className={`${cardM} p-3.5`} data-testid="cut-info">
          <div className="font-mono text-xs font-bold text-slate-800">{spk.number}</div>
          <div className="text-xs font-semibold text-slate-700 mt-0.5">{spk.product_name} • <b>{variant}</b></div>
          <div className="text-[10px] text-slate-400 mt-0.5">Target variasi ini: {vrow.target_qty} pcs</div>
        </div>
        <div className={`${cardM} p-4`}>
          <label className="text-xs font-semibold text-slate-500">Total Hasil Potong</label>
          <div className="relative mt-2">
            <input data-testid="cut-total" type="number" min="1" value={total} onChange={(e) => setTotal(e.target.value)} className={`${inpM} pr-10`} />
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">pcs</span>
          </div>
        </div>
        <div className={`${cardM} p-4 space-y-2.5`}>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-500">Pembagian Bundel</label>
            <button data-testid="cut-bundle-add" onClick={() => setRows([...rows, ""])} className="h-8 px-3 rounded-lg border border-blue-200 text-blue-600 text-[11px] font-semibold">+ Tambah Bundel</button>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 w-16 shrink-0">Bundel {i + 1}</span>
              <div className="relative flex-1">
                <input data-testid={`cut-bundle-${i}`} type="number" min="1" value={r} onChange={(e) => setRows(rows.map((x, j) => (j === i ? e.target.value : x)))} className={`${inpM} pr-10`} />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">pcs</span>
              </div>
              {rows.length > 1 && (
                <button data-testid={`cut-bundle-del-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="w-9 h-9 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          <div data-testid="cut-balance" className={`text-xs font-bold text-center rounded-xl py-2 ${balance === 0 && t > 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
            {t} − {sum} = balance {balance} pcs {balance === 0 && t > 0 ? "✓" : ""}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <button data-testid="cut-draft-btn" onClick={() => save(false)} disabled={busy || !rowsValid || !(t > 0)}
            className="h-12 rounded-2xl border-2 text-sm font-bold disabled:opacity-40" style={{ borderColor: BLUE, color: BLUE }}>Simpan Draft</button>
          <button data-testid="cut-generate-btn" onClick={() => save(true)} disabled={busy || !rowsValid || balance !== 0}
            className="h-12 rounded-2xl text-white text-sm font-bold disabled:opacity-40 shadow-lg shadow-blue-200" style={{ background: BLUE }}>
            {busy ? "Menyimpan..." : "Generate Bundel"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FreeBundlePicker({ bundles, onPick }) {
  const [q, setQ] = useState("");
  const free = bundles.filter((b) => b.status === "ready_jahit" && !b.parent_id && !b.sewer_id && b.ready_qty === b.qty);
  const query = q.trim().toLowerCase();
  const list = query
    ? free.filter((b) => (b.product_name || "").toLowerCase().includes(query) || (b.variant || "").toLowerCase().includes(query) || b.number.toLowerCase().includes(query) || (b.spk_number || "").toLowerCase().includes(query))
    : [];
  return (
    <div className="px-4 pb-4 space-y-3">
      <div className="text-[11px] text-slate-400 leading-snug">Untuk penjahit yang lupa dibagikan tapi sudah setor — pilih bundelnya, lalu pilih penjahitnya. Alur setelah itu sama seperti biasa.</div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="fb-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk, variasi, atau no bundel (status ready)" className={`${inpM} pl-10`} />
      </div>
      <div className="space-y-2.5">
        {list.map((b) => (
          <button key={b.id} data-testid={`fb-pick-${b.id}`} onClick={() => onPick(b.id)} className={`${cardM} w-full p-3.5 text-left`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200 font-semibold whitespace-nowrap">Ready • {b.qty} pcs</span>
            </div>
            <div className="text-xs font-semibold text-slate-700 mt-1 truncate">{b.product_name} • {b.variant}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{b.spk_number} • belum dikerjakan siapa pun</div>
          </button>
        ))}
        {!list.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="fb-empty">{query ? "Tidak ada bundel ready yang cocok." : "Ketik nama produk, variasi, atau no bundel untuk mencari."}</div>}
      </div>
    </div>
  );
}

function SewerPicker({ sewers, counts, onBack, onPick, onFree }) {
  const [q, setQ] = useState("");
  const list = sewers.filter((e) => (e.name + (e.employee_id || "")).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div data-testid="screen-sewers">
      <SubHeader title="Pilih Penjahit" onBack={onBack} testid="sewers-header" />
      <div className="px-4 pb-4 space-y-3">
        {onFree && (
          <button data-testid="sewer-free-link" onClick={onFree} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-sky-50 border border-sky-100 text-left">
            <span className="w-8 h-8 rounded-lg bg-white text-sky-600 flex items-center justify-center shrink-0 border border-sky-100"><Package className="w-4 h-4" /></span>
            <span className="flex-1 text-[11px] font-semibold text-sky-700 leading-snug">Penjahit lupa dibagikan? Cari bundel ready (belum dikerjakan siapa pun) lalu pilih penjahitnya</span>
            <ChevronRight className="w-4 h-4 text-sky-400 shrink-0" />
          </button>
        )}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="sewer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau ID penjahit..." className={`${inpM} pl-10`} />
        </div>
        <p className="text-[11px] text-slate-400 font-semibold">Penjahit Aktif ({list.length})</p>
        <div className={`${cardM} divide-y divide-slate-50 overflow-hidden`}>
          {list.map((e) => (
            <button key={e.id} data-testid={`sewer-card-item-${e.id}`} onClick={() => onPick(e.id)} className="w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-blue-50/40 transition-colors">
              <Avatar name={e.name} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 text-sm">{e.name}</div>
                <div className="text-[11px] text-slate-400">ID: {e.employee_id || "-"}</div>
              </div>
              {counts[e.id] ? <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-semibold border border-blue-100">{counts[e.id]} bundle</span> : null}
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </button>
          ))}
          {!list.length && <p className="py-8 text-center text-xs text-slate-400" data-testid="sewer-empty">{q ? "Tidak ada penjahit cocok." : "Belum ada karyawan berperan penjahit."}</p>}
        </div>
      </div>
    </div>
  );
}

function BundleCard({ b, img, badgeFor, testid }) {
  const sewn = (b.qty || 0) - (b.ready_qty || 0);
  const [lb, cls] = badgeFor(b);
  return (
    <div className={`${cardM} p-3.5`} data-testid={testid}>
      <div className="flex gap-3">
        <ProdThumb img={img} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="font-mono text-xs font-bold text-slate-800">{b.number}</span>
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${cls}`}>{lb}</span>
          </div>
          {b.spk_number && <div className="text-[10px] text-slate-400 font-mono">{b.spk_number}</div>}
          <div className="text-xs font-semibold text-slate-700 truncate mt-0.5">{b.product_name}</div>
          <div className="text-[10px] text-slate-400">{b.variant}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-50 text-center">
        <div><div className="text-sm font-bold text-slate-800">{b.qty} <span className="text-[9px] font-normal text-slate-400">pcs</span></div><div className="text-[9px] text-slate-400">Jumlah Potongan</div></div>
        <div><div className="text-sm font-bold text-emerald-600">{sewn} <span className="text-[9px] font-normal text-slate-400">pcs</span></div><div className="text-[9px] text-slate-400">Sudah Dijahit</div></div>
        <div><div className="text-sm font-bold text-red-500">{b.ready_qty || 0} <span className="text-[9px] font-normal text-slate-400">pcs</span></div><div className="text-[9px] text-slate-400">Sisa</div></div>
      </div>
    </div>
  );
}

function WorkList({ repairs, working, waiting, qcQueue, returnQueue = [], recheckQueue = [], spkQueue = [], employees = [], onPickBundle, onPickQc, onReturn, onRecheck, onPickSpk }) {
  const sewerName = (id) => employees.find((e) => e.id === id)?.name || "-";
  const total = repairs.length + working.length + waiting.length + qcQueue.length + returnQueue.length + recheckQueue.length + spkQueue.length;
  return (
    <div className="space-y-2.5" data-testid="work-list">
      {spkQueue.map((s) => {
        const [lb, cls] = SPK_ST[s.status] || [s.status, "bg-slate-100 text-slate-500 border border-slate-200"];
        const target = (s.variants || []).reduce((a, v) => a + (v.target_qty || 0), 0);
        return (
          <button key={s.id} data-testid={`work-spk-${s.id}`} onClick={() => onPickSpk(s.id)} className={`${cardM} w-full p-3.5 text-left border-l-4 !border-l-blue-400`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold text-slate-700">{s.number}</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${cls}`}>{lb}</span>
            </div>
            <div className="text-xs font-semibold text-slate-700 mt-1 truncate">{s.product_name}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Target {target.toLocaleString("id-ID")} pcs — ketuk untuk baca & proses SPK ini.</div>
          </button>
        );
      })}
      {recheckQueue.map((r) => (
        <button key={r.id} data-testid={`work-recheck-${r.id}`} onClick={() => onRecheck(r.id)} className={`${cardM} w-full p-3.5 text-left border-l-4 !border-l-violet-400`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200 font-semibold">Periksa Ulang • {r.qty} pcs</span>
            <span className="font-mono text-[10px] text-slate-400">{r.bundle_number}</span>
          </div>
          <div className="text-xs font-semibold text-slate-700 mt-1.5">Hasil perbaikan {sewerName(r.sewer_id)} sudah disetor mandor</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Ketuk untuk memeriksa ulang — yang lolos mengembalikan saldo penjahit.</div>
        </button>
      ))}
      {repairs.map((r) => (
        <div key={r.id} data-testid={`work-repair-${r.id}`} className={`${cardM} p-3.5 border-l-4 ${r.status === "disetor" ? "!border-l-sky-400" : "!border-l-amber-400"}`}>
          <div className="flex items-center justify-between gap-2">
            {r.status === "disetor" ? (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200 font-semibold">Sudah disetor mandor — menunggu periksa ulang QC</span>
            ) : (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Perlu Perbaikan • {r.qty} pcs</span>
            )}
            <span className="font-mono text-[10px] text-slate-400">{r.bundle_number}</span>
          </div>
          <div className="text-xs font-semibold text-slate-700 mt-1.5">{r.reason}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{r.status === "disetor" ? "Sudah Anda perbaiki dan disetor — saldo kembali bila lolos periksa ulang." : "Feedback QC — saldo bagian ini ditahan sampai perbaikan lolos periksa ulang."}</div>
        </div>
      ))}
      {returnQueue.map((r) => (
        <div key={r.id} data-testid={`work-return-${r.id}`} className={`${cardM} p-3.5 border-l-4 !border-l-amber-400`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-semibold">Perbaikan Penjahit • {r.qty} pcs</span>
            <span className="font-mono text-[10px] text-slate-400">{r.bundle_number}</span>
          </div>
          <div className="text-xs font-semibold text-slate-700 mt-1.5">{sewerName(r.sewer_id)} — {r.reason}</div>
          <button data-testid={`return-submit-${r.id}`} onClick={() => onReturn(r.id)}
            className="mt-2.5 h-9 px-4 rounded-xl text-white text-xs font-bold" style={{ background: BLUE }}>Setor Hasil Perbaikan ke QC</button>
        </div>
      ))}
      {working.map((b) => {
        const sewn = (b.qty || 0) - (b.ready_qty || 0);
        return (
          <button key={b.id} data-testid={`work-bundle-${b.id}`} onClick={() => onPickBundle(b.id)} className={`${cardM} w-full p-3.5 text-left flex items-center gap-3`}>
            <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Scissors className="w-4 h-4" /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${sewn > 0 ? BADGE.jalan[1] : BADGE.belum[1]}`}>{sewn > 0 ? "Sedang Dikerjakan" : "Belum Dikerjakan"}</span>
              </div>
              <div className="text-xs text-slate-600 truncate mt-0.5">{b.product_name} • {b.variant}</div>
              <div className="text-[10px] text-slate-400">Sisa {b.ready_qty} dari {b.qty} pcs</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
          </button>
        );
      })}
      {waiting.map((b) => (
        <div key={b.id} data-testid={`work-waitqc-${b.id}`} className={`${cardM} p-3.5 flex items-center gap-3`}>
          <span className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0"><ClipboardCheck className="w-4 h-4" /></span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200 font-semibold whitespace-nowrap">Menunggu QC</span>
            </div>
            <div className="text-xs text-slate-600 truncate mt-0.5">{b.product_name} • {b.variant}</div>
            <div className="text-[10px] text-slate-400">{b.qty} pcs disetor — upah sudah masuk saldo, menunggu hasil pemeriksaan.</div>
          </div>
        </div>
      ))}
      {qcQueue.map((b) => (
        <button key={b.id} data-testid={`work-qc-${b.id}`} onClick={() => onPickQc(b.id)} className={`${cardM} w-full p-3.5 text-left flex items-center gap-3`}>
          <span className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><ClipboardCheck className="w-4 h-4" /></span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-semibold whitespace-nowrap">Perlu Diperiksa • {b.qty} pcs</span>
            </div>
            <div className="text-xs text-slate-600 truncate mt-0.5">{b.product_name} • {b.variant}</div>
            <div className="text-[10px] text-slate-400">Penjahit: {b.sewer_name}</div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
        </button>
      ))}
      {!total && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="work-empty">Belum ada pekerjaan aktif untuk Anda.</div>}
    </div>
  );
}

function RecheckFormScreen({ repair, employees, onBack, onDone }) {
  const sewerName = employees.find((e) => e.id === repair.sewer_id)?.name || "-";
  const [f, setF] = useState({ passed: "", reject: "", note: "", reject_reason: "", reject_photo: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const p = (k) => parseInt(f[k]) || 0;
  const sum = p("passed") + p("reject");
  const valid = sum === repair.qty;
  const upload = async (e) => {
    const fl = e.target.files?.[0]; e.target.value = "";
    if (!fl) return;
    try { const fd = new FormData(); fd.append("file", fl); const { data } = await api.post("/uploads", fd); setF((s) => ({ ...s, reject_photo: data.url })); toast.success("Foto terunggah"); }
    catch { toast.error("Gagal mengunggah foto"); }
  };
  const submit = async () => {
    if (!valid) return toast.error(`Lolos + reject harus sama dengan qty perbaikan (${repair.qty} pcs)`);
    if (p("reject") > 0 && !f.reject_reason.trim()) return toast.error("Alasan reject wajib diisi");
    setBusy(true);
    try {
      await api.post("/prod/qc/recheck", { repair_id: repair.id, passed: p("passed"), reject: p("reject"), note: f.note, reject_reason: f.reject_reason, reject_photo: f.reject_photo || null });
      onDone(p("passed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan periksa ulang"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="screen-recheck-form">
      <SubHeader title="Periksa Ulang Perbaikan" onBack={onBack} testid="recheck-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <div className={`${cardM} p-3.5`} data-testid="recheck-repair-card">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs font-bold text-slate-800">{repair.bundle_number}</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200 font-semibold">Perbaikan {repair.qty} pcs</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">Penjahit: <b>{sewerName}</b> • Disetor oleh {repair.returned_by || "mandor"}</div>
          <div className="text-[11px] text-slate-500 mt-1 bg-amber-50 border border-amber-100 rounded-lg p-2">Alasan perbaikan: {repair.reason}</div>
        </div>
        <div className={`${cardM} p-4 space-y-3`}>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold text-emerald-600">Lolos Perbaikan</label><input data-testid="rc-passed" type="number" min="0" value={f.passed} onChange={(e) => setF({ ...f, passed: e.target.value })} className={inpM} /></div>
            <div><label className="text-[11px] font-semibold text-red-500">Reject Permanen</label><input data-testid="rc-reject" type="number" min="0" value={f.reject} onChange={(e) => setF({ ...f, reject: e.target.value })} className={inpM} /></div>
          </div>
          <div data-testid="rc-sum" className={`text-xs font-bold text-center rounded-xl py-2 ${valid ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
            {p("passed")} + {p("reject")} = {sum} {valid ? `= ${repair.qty} pcs ✓` : `≠ ${repair.qty} pcs`}
          </div>
          <input data-testid="rc-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Catatan (opsional)" className={inpM} />
          {p("reject") > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">Reject permanen: pengawas yang menentukan besar potongan.</div>
              <input data-testid="rc-reject-reason" value={f.reject_reason} onChange={(e) => setF({ ...f, reject_reason: e.target.value })} placeholder="Alasan reject *" className={inpM} />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
              <button data-testid="rc-reject-photo" onClick={() => fileRef.current?.click()} className="w-full h-10 px-3 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500 flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> {f.reject_photo ? "Foto terlampir" : "Unggah foto bukti *"}</button>
            </div>
          )}
          <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5">Yang lolos: saldo {sewerName} yang ditahan langsung kembali.</div>
        </div>
        <button data-testid="rcf-submit" onClick={submit} disabled={busy || !valid}
          className="w-full h-12 rounded-2xl text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-blue-200" style={{ background: BLUE }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Simpan Periksa Ulang
        </button>
      </div>
    </div>
  );
}

function AbsenScreen({ onBack }) {
  const { user } = useAuth();
  const [att, setAtt] = useState([]);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null);
  const [gpsMsg, setGpsMsg] = useState("");
  const [cam, setCam] = useState(null); // { type: 'in'|'out' }
  const PROD_ROLES = ["penjahit", "cutting", "finishing_qc", "mandor", "pengawas_produksi"];
  const isProduction = PROD_ROLES.includes(user?.role); // karyawan produksi: absen lama (tanpa wajib selfie+GPS)
  const load = useCallback(() => {
    api.get("/attendance/me").then((r) => setAtt(r.data)).catch(() => {});
    api.get("/attendance/today-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!isProduction) grabGps(); }, [isProduction]); // GPS wajib hanya untuk karyawan tetap
  const record = info?.record;
  const grabGps = () => new Promise((resolve) => {
    if (!navigator.geolocation) { setGpsMsg("GPS tidak tersedia di perangkat ini"); return resolve(null); }
    navigator.geolocation.getCurrentPosition(
      (p) => { const v = { lat: p.coords.latitude, lng: p.coords.longitude, acc: Math.round(p.coords.accuracy) }; setPos(v); setGpsMsg(""); resolve(v); },
      () => { setGpsMsg("Izin lokasi ditolak — GPS wajib aktif untuk absen"); resolve(null); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
  const clock = (type) => {
    if (isProduction) { doClockProduction(type); return; }
    if (!pos) { toast.error("GPS wajib aktif — tekan 'Aktifkan / Segarkan GPS' lalu izinkan lokasi"); grabGps(); return; }
    setCam({ type });
  };
  const doClockProduction = async (type) => {
    setBusy(true);
    try {
      await api.post("/attendance/clock", { type });
      toast.success(type === "in" ? "Absen masuk berhasil" : "Absen pulang berhasil");
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Gagal absen"); } finally { setBusy(false); }
  };
  const doClock = async (type, selfieFile) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selfieFile, `selfie-${type}-${Date.now()}.jpg`);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/attendance/clock", { type, lat: pos.lat, lng: pos.lng, selfie_url: up.data.url });
      toast.success(type === "in" ? "Absen masuk berhasil" : "Absen pulang berhasil");
      setCam(null);
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Gagal absen"); } finally { setBusy(false); }
  };
  const DAY_NAME = { sen: "Senin", sel: "Selasa", rab: "Rabu", kam: "Kamis", jum: "Jumat", sab: "Sabtu", min: "Minggu" };
  const badge = (a) => a.off_schedule ? ["Luar Jadwal", "bg-sky-100 text-sky-700"] : a.status === "terlambat" ? [`Terlambat ${a.late_minutes || ""} mnt`, "bg-amber-100 text-amber-700"] : ["Hadir", "bg-emerald-100 text-emerald-700"];
  return (
    <div data-testid="screen-absen">
      <SubHeader title="Absensi" onBack={onBack} testid="absen-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <div className={`${cardM} p-4`} data-testid="absen-schedule-card">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Jadwal Hari Ini ({DAY_NAME[info?.day_key] || ""})</div>
          {info ? (info.is_workday
            ? <div className="font-bold text-slate-900 text-base mt-1">{user?.role_label} • {info.shift_start} – {info.shift_end}</div>
            : <div className="font-bold text-sky-600 text-base mt-1" data-testid="absen-libur">LIBUR — absen tetap bisa, ditandai &quot;Luar Jadwal&quot;</div>)
            : <div className="text-sm text-slate-400 mt-1">Memuat jadwal...</div>}
          <div className="flex gap-1.5 mt-2">
            {record?.clock_in_in_area === true && <span className="text-[9px] font-bold bg-emerald-50 text-emerald-600 rounded-full px-2 py-1">Dalam Area Kerja</span>}
            {record?.clock_in_in_area === false && <span className="text-[9px] font-bold bg-amber-50 text-amber-600 rounded-full px-2 py-1">Luar Area</span>}
          </div>
        </div>
        <div className={`${cardM} p-4 space-y-3`}>
          {!isProduction && (
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <button data-testid="absen-gps-btn" onClick={grabGps} className="font-semibold text-blue-600">Aktifkan / Segarkan GPS</button>
              <span>{pos ? `Akurasi ±${pos.acc} m` : gpsMsg || "GPS belum aktif"}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="absen-clock-in-btn" disabled={busy || (!isProduction && !pos) || !!record?.clock_in} onClick={() => clock("in")} className="h-12 rounded-xl text-white font-bold text-sm disabled:opacity-40" style={{ background: BLUE }}>{record?.clock_in ? `Masuk ${formatTimeID(record.clock_in)}` : "Absen Masuk"}</button>
            <button data-testid="absen-clock-out-btn" disabled={busy || (!isProduction && !pos) || !record?.clock_in || !!record?.clock_out} onClick={() => clock("out")} className="h-12 rounded-xl bg-amber-500 text-white font-bold text-sm disabled:opacity-40">{record?.clock_out ? `Pulang ${formatTimeID(record.clock_out)}` : "Absen Pulang"}</button>
          </div>
          {!isProduction && <p className="text-[10px] text-slate-400 text-center">Selfie + GPS wajib untuk absen.</p>}
          {isProduction && <p className="text-[10px] text-slate-400 text-center">Karyawan produksi — absen langsung tanpa selfie/GPS.</p>}
          {record?.status === "terlambat" && <p className="text-[11px] text-amber-600 font-semibold text-center">Tercatat terlambat {record.late_minutes} menit (toleransi 10 menit).</p>}
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-400 mb-2">Riwayat 30 hari</div>
          <div className={`${cardM} divide-y divide-slate-50 overflow-hidden`} data-testid="absen-history">
            {att.slice(0, 10).map((a) => { const [lb, cls] = badge(a); return (
              <div key={a.id} className="px-3.5 py-2.5 flex justify-between items-center">
                <div><div className="text-xs font-semibold text-slate-700">{formatDateID(a.date)}</div><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{lb}</span></div>
                <div className="text-[11px] text-slate-500">{a.clock_in ? formatTimeID(a.clock_in) : "—"} → {a.clock_out ? formatTimeID(a.clock_out) : "—"}</div>
              </div>); })}
            {!att.length && <div className="py-6 text-center text-xs text-slate-400">Belum ada riwayat absensi.</div>}
          </div>
        </div>
      </div>
      {cam && <SelfieCamera type={cam.type} busy={busy} onCapture={(f) => doClock(cam.type, f)} onClose={() => !busy && setCam(null)} />}
    </div>
  );
}

function SelfieCamera({ type, busy, onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null); // { url, blob }
  useEffect(() => {
    let alive = true;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((s) => { if (!alive) { s.getTracks().forEach((t) => t.stop()); return; } streamRef.current = s; if (videoRef.current) videoRef.current.srcObject = s; })
      .catch(() => setErr("Kamera tidak tersedia / izin ditolak"));
    return () => { alive = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);
  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => { if (b) setPreview({ url: URL.createObjectURL(b), blob: b }); }, "image/jpeg", 0.85);
  };
  const retake = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };
  return (
    <div data-testid="selfie-modal" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full max-w-md rounded-3xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-sm">Selfie Absen {type === "in" ? "Masuk" : "Pulang"}</h3>
          <button data-testid="selfie-close" onClick={() => !busy && onClose()} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
        </div>
        {err ? (
          <div className="aspect-square rounded-2xl bg-slate-100 flex flex-col items-center justify-center text-slate-400 text-xs p-6 text-center">
            <Camera className="w-8 h-8 mb-2" />{err}
          </div>
        ) : preview ? (
          <img src={preview.url} alt="Selfie" className="w-full aspect-square object-cover rounded-2xl bg-slate-100" data-testid="selfie-preview" />
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-square object-cover rounded-2xl bg-slate-900" data-testid="selfie-video" />
        )}
        <div className="grid grid-cols-2 gap-2">
          {preview ? (
            <>
              <button data-testid="selfie-retake" onClick={retake} disabled={busy} className="h-11 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold disabled:opacity-40">Ambil Ulang</button>
              <button data-testid="selfie-submit" onClick={() => onCapture(preview.blob)} disabled={busy} className="h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: BLUE }}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Gunakan Foto
              </button>
            </>
          ) : (
            <button data-testid="selfie-capture" onClick={capture} disabled={!!err} className="col-span-2 h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: BLUE }}>
              <Camera className="w-4 h-4" /> Ambil Foto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function IzinScreen({ onBack }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/leave/me").then((r) => setData(r.data)).catch(() => setData({ items: [], balance: null })), []);
  useEffect(() => { load(); }, [load]);
  const cancel = async (id) => {
    try { await api.put(`/leave/${id}/cancel`); toast.success("Pengajuan dibatalkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
  };
  const items = data?.items || [];
  const bal = data?.balance;
  return (
    <div data-testid="screen-izin">
      <SubHeader title="Izin & Cuti" onBack={onBack} testid="izin-header" />
      <div className="px-4 space-y-3 pb-6">
        {bal && (
          <div className={`${cardM} p-4 flex items-center justify-between`} data-testid="izin-balance">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Sisa Saldo Izin {bal.year}</div>
              <div className="text-xl font-bold text-blue-700">{bal.remaining} <span className="text-xs font-normal text-slate-400">/ {bal.quota} hari</span></div>
            </div>
            <div className="text-[10px] text-slate-400 text-right">Terpakai<br /><b className="text-slate-600 text-sm">{bal.used} hari</b></div>
          </div>
        )}
        <button data-testid="izin-new-btn" onClick={() => setShowForm(true)} className="w-full h-12 rounded-2xl text-white text-sm font-bold shadow-lg shadow-blue-200" style={{ background: BLUE }}>+ Ajukan Izin Baru</button>
        {items.map((i) => (
          <div key={i.id} data-testid={`izin-item-${i.id}`} className={`${cardM} p-4 space-y-1.5`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">{TYPE_LABEL[i.type] || i.type}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[i.status] || "bg-slate-100 text-slate-500"}`}>{ST_LABEL[i.status] || i.status}</span>
            </div>
            <div className="text-xs text-slate-500">{formatDateID(i.start_date)}{i.end_date !== i.start_date ? ` — ${formatDateID(i.end_date)}` : ""} • {DUR_LABEL[i.duration] || i.duration}</div>
            {i.status === "disetujui" && <div className="text-[10px] font-semibold text-emerald-600">{i.paid ? "Dibayar — tanpa potongan gaji" : "Potong Gaji — masuk komponen Potongan Izin"}</div>}
            {i.status === "ditolak" && i.hr_note && <div className="text-[10px] text-red-500">Catatan HR: {i.hr_note}</div>}
            {i.status === "menunggu" && (
              <button data-testid={`izin-cancel-${i.id}`} onClick={() => cancel(i.id)} className="text-[11px] font-semibold text-red-500">Batalkan Pengajuan</button>
            )}
          </div>
        ))}
        {data && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="izin-empty">Belum ada pengajuan.</div>}
      </div>
      {showForm && <LeaveForm tid="izinf" onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}

function KasbonScreen({ onBack }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ amount: "", installments: "1", reason: "" });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/kasbon/me").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const ST = { diajukan: ["Menunggu Persetujuan", "bg-amber-50 text-amber-600"], disetujui: ["Disetujui", "bg-emerald-50 text-emerald-600"], ditolak: ["Ditolak", "bg-red-50 text-red-600"], lunas: ["Lunas", "bg-sky-50 text-sky-600"], belum_lunas: ["Belum Lunas", "bg-orange-50 text-orange-600"] };
  const submit = async () => {
    if (!(parseInt(form.amount) >= 10000)) return toast.error("Nominal minimal Rp10.000");
    setBusy(true);
    try {
      await api.post("/kasbon", { amount: parseInt(form.amount), installments: parseInt(form.installments) || 1, reason: form.reason });
      toast.success("Kasbon diajukan ke HR"); setShowForm(false); setForm({ amount: "", installments: "1", reason: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="screen-kasbon">
      <SubHeader title="Kasbon" onBack={onBack} testid="kasbon-header" />
      <div className="px-4 space-y-3 pb-6">
        <button data-testid="kasbon-new-btn" onClick={() => setShowForm(true)} className="w-full h-12 rounded-2xl text-white text-sm font-bold shadow-lg shadow-blue-200" style={{ background: BLUE }}>+ Ajukan Kasbon</button>
        {(items || []).map((k) => {
          const [lb, cls] = ST[k.status] || [k.status, "bg-slate-100 text-slate-500"];
          return (
            <div key={k.id} data-testid={`kasbon-item-${k.id}`} className={`${cardM} p-4`}>
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <span className="text-sm font-bold text-slate-800">{formatIDR(k.amount)}</span>
                  {k.type === "dipotong_setelah_pekerjaan" && (
                    <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-semibold">Dipotong Setelah Pekerjaan Selesai</span>
                  )}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${cls}`}>{lb}</span>
              </div>
              {k.type === "dipotong_setelah_pekerjaan" ? (
                <div className="text-xs text-slate-500 mt-1.5 space-y-0.5">
                  <div className="font-semibold text-slate-600">{k.ref_number} • {k.bundle_product} {k.bundle_variant ? `(${k.bundle_variant})` : ""} • {k.bundle_qty} pcs</div>
                  <div>Nilai upah pekerjaan: {formatIDR(k.bundle_upah || 0)}</div>
                  <div className="flex items-center gap-2">
                    <span>Kasbon terikat: <b className="text-orange-600">−{formatIDR(k.amount)}</b></span>
                    {k.status !== "lunas" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 font-bold">Belum Lunas</span>}
                    {k.status === "lunas" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-600 font-bold">Lunas</span>}
                  </div>
                  {(k.history || []).length > 0 && (
                    <details className="text-[10px] text-slate-400 mt-1">
                      <summary className="cursor-pointer font-semibold text-slate-500">Riwayat ({k.history.length})</summary>
                      <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-slate-100">
                        {k.history.map((h, i) => (
                          <div key={i}>• {h.action.replace(/_/g, " ")}{h.potong ? ` Rp${h.potong.toLocaleString("id-ID")}` : h.amount ? ` Rp${h.amount.toLocaleString("id-ID")}` : ""} — {h.by} <span className="text-slate-300">({(h.at || "").slice(0, 10)})</span></div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ) : (
                <div className="text-xs text-slate-500 mt-1">Angsuran {k.installments}× • terbayar {k.paid_count || 0}× • {k.reason || "-"}</div>
              )}
              <div className="text-[10px] text-slate-400 mt-0.5">{formatDateID(k.created_at)}</div>
            </div>
          );
        })}
        {items !== null && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="kasbon-empty">Belum ada kasbon.</div>}
      </div>
      {showForm && <KasbonFormModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} busy={busy} setBusy={setBusy} />}
    </div>
  );
}

function KasbonFormModal({ onClose, onSaved, busy, setBusy }) {
  const [jenis, setJenis] = useState("umum");
  const [form, setForm] = useState({ amount: "", installments: "1", reason: "" });
  const [bundles, setBundles] = useState(null);
  const [bundleId, setBundleId] = useState("");
  const [mode, setMode] = useState("seluruh");
  const [customAmount, setCustomAmount] = useState("");
  useEffect(() => {
    if (jenis === "dipotong") {
      api.get("/prod/kasbon/bundle-options").then((r) => setBundles(r.data.items || [])).catch(() => setBundles([]));
    }
  }, [jenis]);
  const bundle = (bundles || []).find((b) => b.id === bundleId);
  const nominal = jenis === "dipotong"
    ? (mode === "seluruh" ? (bundle?.max_kasbon || 0) : parseInt(String(customAmount).replace(/\D/g, ""), 10) || 0)
    : parseInt(String(form.amount).replace(/\D/g, ""), 10) || 0;

  const submit = async () => {
    if (jenis === "umum") {
      if (!(nominal >= 10000)) return toast.error("Nominal minimal Rp10.000");
      setBusy(true);
      try {
        await api.post("/kasbon", { amount: nominal, installments: parseInt(form.installments) || 1, reason: form.reason });
        toast.success("Kasbon diajukan ke HR"); onSaved();
      } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); } finally { setBusy(false); }
      return;
    }
    if (!bundleId) return toast.error("Pilih pekerjaan/bundel dulu");
    if (!(nominal > 0)) return toast.error("Nominal kasbon harus lebih dari Rp0");
    setBusy(true);
    try {
      await api.post("/kasbon/dipotong-setelah-pekerjaan", { bundle_id: bundleId, amount: nominal, reason: form.reason });
      toast.success("Kasbon diajukan — Menunggu Persetujuan"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="kasbon-modal" className="fixed inset-0 z-50 bg-slate-900/60 flex items-end justify-center" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full max-w-md rounded-t-3xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-base">Ajukan Kasbon</h3>
          <button data-testid="kasbon-close" onClick={() => !busy && onClose()} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2" data-testid="kasbon-jenis">
          <button data-testid="kasbon-jenis-umum" onClick={() => setJenis("umum")}
            className={`h-11 rounded-xl text-xs font-bold border ${jenis === "umum" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>Kasbon Umum</button>
          <button data-testid="kasbon-jenis-dipotong" onClick={() => setJenis("dipotong")}
            className={`h-11 rounded-xl text-[11px] font-bold border leading-tight px-1 ${jenis === "dipotong" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>Dipotong Setelah Pekerjaan Selesai</button>
        </div>

        {jenis === "umum" && (
          <>
            <input data-testid="kasbon-amount" type="number" min="10000" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Nominal (Rp) — minimal Rp10.000" className={inpM} />
            <input data-testid="kasbon-installments" type="number" min="1" max="24" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} placeholder="Jumlah angsuran (bulan)" className={inpM} />
            <textarea data-testid="kasbon-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} placeholder="Alasan" className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
          </>
        )}

        {jenis === "dipotong" && (
          <>
            {bundles === null ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
            ) : (
              <div>
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Pekerjaan / Bundel (dibagikan mandor, belum selesai)</label>
                <select data-testid="kasbon-bundle" value={bundleId} onChange={(e) => setBundleId(e.target.value)} className={inpM}>
                  <option value="">— Pilih pekerjaan —</option>
                  {bundles.map((b) => <option key={b.id} value={b.id} disabled={b.max_kasbon <= 0}>{b.number} — {b.product_name} {b.variant ? `(${b.variant})` : ""} • {b.qty} pcs{b.max_kasbon <= 0 ? " (sudah penuh)" : ""}</option>)}
                </select>
                {!bundles.length && <p className="text-[10px] text-amber-600 mt-1">Tidak ada bundel yang dibagikan mandor & belum selesai.</p>}
              </div>
            )}
            {bundle && (
              <>
                <div className="grid grid-cols-2 gap-2" data-testid="kasbon-mode">
                  <button data-testid="kasbon-mode-seluruh" onClick={() => setMode("seluruh")}
                    className={`h-10 rounded-xl text-xs font-bold border ${mode === "seluruh" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>Seluruh nilai upah</button>
                  <button data-testid="kasbon-mode-manual" onClick={() => setMode("manual")}
                    className={`h-10 rounded-xl text-xs font-bold border ${mode === "manual" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>Tulis nominal sendiri</button>
                </div>
                {mode === "manual" && (
                  <input data-testid="kasbon-custom-amount" inputMode="numeric" value={customAmount} onChange={(e) => setCustomAmount(e.target.value.replace(/\D/g, ""))} placeholder={`Nominal (maks ${formatIDR(bundle.max_kasbon)})`} className={inpM} />
                )}
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3.5 space-y-1 text-xs" data-testid="kasbon-summary">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Ringkasan Pekerjaan</div>
                  <div className="flex justify-between"><span className="text-slate-500">Pekerjaan</span><span className="font-semibold text-slate-700">{bundle.number}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Produk</span><span className="font-semibold text-slate-700 text-right">{bundle.product_name}{bundle.variant ? ` (${bundle.variant})` : ""}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Jumlah</span><span className="font-semibold text-slate-700">{bundle.qty} pcs</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Nilai upah pekerjaan</span><span className="font-semibold text-slate-700">{formatIDR(bundle.total_upah)}</span></div>
                  {bundle.tied_kasbon > 0 && <div className="flex justify-between"><span className="text-slate-500">Kasbon terikat sebelumnya</span><span className="font-semibold text-orange-600">−{formatIDR(bundle.tied_kasbon)}</span></div>}
                  <div className="flex justify-between pt-1.5 border-t border-slate-200 text-sm"><span className="font-bold text-slate-800">Nominal kasbon</span><span className="font-bold text-blue-600">{formatIDR(nominal)}</span></div>
                </div>
              </>
            )}
            <textarea data-testid="kasbon-reason-dipotong" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} placeholder="Alasan (opsional)" className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
          </>
        )}
        <button data-testid="kasbon-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-bold disabled:opacity-40" style={{ background: BLUE }}>{busy ? "Mengirim..." : "Oke, Ajukan"}</button>
      </div>
    </div>
  );
}

function AssignBundleModal({ bundles, employees, onClose, onSaved, busy, setBusy }) {
  const [sewerQ, setSewerQ] = useState("");
  const [sewerId, setSewerId] = useState("");
  const [bq, setBq] = useState("");
  const [bundleId, setBundleId] = useState("");
  const ready = useMemo(() => bundles.filter((b) => b.status === "ready_jahit" && !b.parent_id && !b.sewer_id && (b.ready_qty || 0) > 0), [bundles]);
  const pool = useMemo(() => employees.filter((e) => e.role === "penjahit"), [employees]);
  const sewerOpts = useMemo(() => {
    const q = sewerQ.trim().toLowerCase();
    return q ? pool.filter((e) => (e.name + (e.employee_id || "") + (e.role_label || "")).toLowerCase().includes(q)).slice(0, 8) : [];
  }, [sewerQ, pool]);
  const bOpts = useMemo(() => {
    const q = bq.trim().toLowerCase();
    return q ? ready.filter((b) => `${b.product_name} ${b.variant} ${b.number} ${b.spk_number || ""}`.toLowerCase().includes(q)).slice(0, 20) : [];
  }, [bq, ready]);
  const sewer = employees.find((e) => e.id === sewerId);
  const bundle = bundles.find((b) => b.id === bundleId);

  const submit = async () => {
    if (!sewerId) return toast.error("Pilih penjahit dulu");
    if (!bundleId) return toast.error("Pilih bundel ready dulu");
    setBusy(true);
    try {
      const r = await api.post(`/prod/bundles/${bundleId}/assign`, { sewer_id: sewerId });
      toast.success(`Bundel ${r.data.number} dibagikan ke ${r.data.sewer_name} — status Di Penjahit`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membagikan bundel"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => !busy && onClose()} data-testid="assign-overlay">
      <div className="bg-white w-full max-w-md rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 text-sm">Bagikan Bundel ke Penjahit</h3>
          <button data-testid="assign-close" onClick={() => !busy && onClose()} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3.5">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 mb-1 block">ID / Nama Penjahit</label>
            {sewer ? (
              <div data-testid="assign-sewer-sel" className="flex items-center justify-between gap-2 h-11 px-3.5 rounded-xl border border-blue-200 bg-blue-50/60">
                <span className="text-xs font-semibold text-slate-700 truncate">{sewer.name} <span className="text-slate-400 font-normal">• {sewer.employee_id || sewer.role_label}</span></span>
                <button data-testid="assign-sewer-clear" onClick={() => { setSewerId(""); setSewerQ(""); }} className="text-slate-400 shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="assign-sewer-q" value={sewerQ} onChange={(e) => setSewerQ(e.target.value)} placeholder="Cari nama atau ID karyawan" className={`${inpM} pl-9`} />
                </div>
                {sewerQ.trim() && (
                  <div data-testid="assign-sewer-list" className="mt-1.5 border border-slate-200 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
                    {sewerOpts.map((e) => (
                      <button key={e.id} data-testid={`assign-sewer-${e.id}`} onClick={() => setSewerId(e.id)}
                        className="w-full text-left px-3.5 py-2.5 text-xs border-b border-slate-50 last:border-0 active:bg-blue-50">
                        <span className="font-semibold text-slate-700">{e.name}</span> <span className="text-slate-400">• {e.employee_id || e.role_label}</span>
                      </button>
                    ))}
                    {!sewerOpts.length && <p className="px-3.5 py-2.5 text-[10px] text-slate-400">Tidak ada karyawan cocok.</p>}
                  </div>
                )}
              </>
            )}
            {!pool.length && <p className="text-[10px] text-amber-600 mt-1">Belum ada karyawan role Penjahit — atur role di menu Karyawan.</p>}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Bundel Ready (belum dibagikan)</label>
            {bundle ? (
              <div data-testid="assign-bundle-sel" className="flex items-center justify-between gap-2 min-h-11 px-3.5 py-2 rounded-xl border border-emerald-200 bg-emerald-50/60">
                <span className="text-xs text-slate-700"><span className="font-mono font-semibold">{bundle.number}</span> — {bundle.product_name} {bundle.variant} • <b>{bundle.ready_qty} pcs</b></span>
                <button data-testid="assign-bundle-clear" onClick={() => { setBundleId(""); setBq(""); }} className="text-slate-400 shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="assign-bundle-q" value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Ketik nama produk / variasi / kode bundel" className={`${inpM} pl-9`} />
                </div>
                {bq.trim() && (
                  <div data-testid="assign-bundle-list" className="mt-1.5 border border-slate-200 rounded-xl overflow-hidden max-h-44 overflow-y-auto">
                    {bOpts.map((b) => (
                      <button key={b.id} data-testid={`assign-bundle-${b.id}`} onClick={() => setBundleId(b.id)}
                        className="w-full text-left px-3.5 py-2.5 text-xs border-b border-slate-50 last:border-0 active:bg-emerald-50">
                        <span className="font-mono font-semibold text-slate-700">{b.number}</span>
                        <span className="text-slate-500"> — {b.product_name} {b.variant} • {b.ready_qty} pcs</span>
                      </button>
                    ))}
                    {!bOpts.length && <p className="px-3.5 py-2.5 text-[10px] text-slate-400">Tidak ada bundel ready cocok.</p>}
                  </div>
                )}
              </>
            )}
            {!ready.length && <p className="text-[10px] text-slate-400 mt-1">Tidak ada bundel ready — generate dulu di Potong & Bundel.</p>}
          </div>
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">Bundel berstatus Di Penjahit setelah dibagikan. Hasil selesai dicatat lewat tombol Input Hasil Jahit.</p>
          <button data-testid="assign-submit" onClick={submit} disabled={busy}
            className="w-full h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: BLUE }}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Bagikan Bundel
          </button>
        </div>
      </div>
    </div>
  );
}

const UPAH_PERIODE = [
  { id: "tanggal", label: "Tanggal" },
  { id: "mingguan", label: "Mingguan" },
  { id: "bulanan", label: "Bulanan" },
  { id: "tahunan", label: "Tahunan" },
];

function upahRange(mode, anchor) {
  // anchor: "YYYY-MM-DD" (tanggal), "YYYY-Www" (mingguan), "YYYY-MM" (bulanan), "YYYY" (tahunan)
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (mode === "tanggal") return { from: anchor, to: anchor };
  if (mode === "mingguan") {
    const [y, w] = anchor.split("-W").map(Number);
    const jan4 = new Date(y, 0, 4);
    const mon = new Date(jan4);
    mon.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (w - 1) * 7);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: iso(mon), to: iso(sun) };
  }
  if (mode === "bulanan") {
    const [y, m] = anchor.split("-").map(Number);
    return { from: `${y}-${pad(m)}-01`, to: iso(new Date(y, m, 0)) };
  }
  return { from: `${anchor}-01-01`, to: `${anchor}-12-31` };
}

function upahAnchorNow(mode) {
  const n = new Date();
  const wib = new Date(n.toLocaleString("en-US", { timeZone: WIB }));
  const pad = (x) => String(x).padStart(2, "0");
  if (mode === "tanggal") return `${wib.getFullYear()}-${pad(wib.getMonth() + 1)}-${pad(wib.getDate())}`;
  if (mode === "mingguan") {
    const d = new Date(Date.UTC(wib.getFullYear(), wib.getMonth(), wib.getDate()));
    const day = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - day + 3);
    const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round(((d - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
    return `${d.getUTCFullYear()}-W${pad(week)}`;
  }
  if (mode === "bulanan") return `${wib.getFullYear()}-${pad(wib.getMonth() + 1)}`;
  return `${wib.getFullYear()}`;
}

function upahRangeLabel(mode, anchor, from, to) {
  const fmt = (iso) => new Date(`${iso}T00:00:00+07:00`).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: WIB });
  const fmtShort = (iso) => new Date(`${iso}T00:00:00+07:00`).toLocaleDateString("id-ID", { day: "numeric", month: "long", timeZone: WIB });
  if (mode === "tanggal") return fmt(from);
  if (mode === "tahunan") return `Tahun ${anchor}`;
  if (mode === "bulanan") return new Date(`${from}T00:00:00+07:00`).toLocaleDateString("id-ID", { month: "long", year: "numeric", timeZone: WIB });
  return `${fmtShort(from)} – ${fmt(to)}`;
}

function UpahScreen({ onBack, user, hasPerm }) {
  const [mode, setMode] = useState("bulanan");
  const [anchor, setAnchor] = useState(() => upahAnchorNow("bulanan"));
  const [tab, setTab] = useState("semua");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adj, setAdj] = useState(null); // { direction }
  const [adjForm, setAdjForm] = useState({ employee_id: "", amount: "", note: "", method: "Transfer BCA" });
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [slipBusy, setSlipBusy] = useState(false);
  const canAdjust = hasPerm && hasPerm("production.wages");

  const downloadSlip = async () => {
    setSlipBusy(true);
    try {
      const r = await api.get("/prod/wages/slip.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `slip-gaji-${(user?.name || "saya").replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Slip gaji berhasil diunduh");
    } catch { toast.error("Belum ada pencairan — slip tersedia setelah saldo dicairkan"); } finally { setSlipBusy(false); }
  };

  const range = useMemo(() => upahRange(mode, anchor), [mode, anchor]);
  const load = useCallback(() => {
    api.get("/prod/wages/me", { params: { period_from: range.from, period_to: range.to } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canAdjust) api.get("/prod/options").then((r) => setEmployees(r.data.employees || [])).catch(() => {});
  }, [canAdjust]);

  const switchMode = (m) => { setMode(m); setAnchor(upahAnchorNow(m)); };
  const txs = useMemo(() => (data?.transactions || []).filter((t) => tab === "semua" || t.direction === tab), [data, tab]);
  const groups = useMemo(() => {
    const g = {};
    txs.forEach((t) => { (g[t.date] = g[t.date] || []).push(t); });
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [txs]);

  const submitAdj = async () => {
    if (!adjForm.employee_id) return toast.error("Pilih karyawan dulu");
    const amount = parseInt(String(adjForm.amount).replace(/\D/g, ""), 10);
    if (!(amount > 0)) return toast.error("Nominal wajib diisi");
    if (!adjForm.note.trim()) return toast.error("Catatan wajib diisi");
    setBusy(true);
    try {
      await api.post("/prod/wages/manual-adjustment", {
        employee_id: adjForm.employee_id, direction: adj.direction,
        amount, note: adjForm.note.trim(), method: adj.direction === "keluar" ? adjForm.method : "",
      });
      toast.success(adj.direction === "masuk" ? "Tambahan saldo tercatat" : "Potongan saldo tercatat — saldo karyawan berkurang");
      setAdj(null); setAdjForm({ employee_id: "", amount: "", note: "", method: "Transfer BCA" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan penyesuaian"); } finally { setBusy(false); }
  };

  const inputType = { tanggal: "date", mingguan: "week", bulanan: "month", tahunan: "number" }[mode];

  return (
    <div data-testid="screen-upah">
      <SubHeader title="Upah" onBack={onBack} testid="upah-header"
        right={(
          <button data-testid="upah-slip-btn" onClick={downloadSlip} disabled={slipBusy}
            className="h-9 px-3 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-600 flex items-center gap-1.5 disabled:opacity-40 shrink-0">
            {slipBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Slip
          </button>
        )} />
      <div className="px-4 space-y-3 pb-24">
        {/* Filter Periode */}
        <div className={`${cardM} p-3.5`} data-testid="upah-filter-card">
          <div className="text-xs font-bold text-slate-800 mb-2.5">Filter Periode</div>
          <div className="grid grid-cols-4 bg-slate-100 rounded-xl p-1" data-testid="upah-periode-tabs">
            {UPAH_PERIODE.map((p) => (
              <button key={p.id} data-testid={`upah-periode-${p.id}`} onClick={() => switchMode(p.id)}
                className={`h-8 rounded-lg text-[11px] font-semibold transition-colors ${mode === p.id ? "bg-blue-600 text-white shadow" : "text-slate-500"}`}>{p.label}</button>
            ))}
          </div>
          <div className="relative mt-2.5">
            <input type={inputType} min={mode === "tahunan" ? 2020 : undefined} max={mode === "tahunan" ? 2100 : undefined}
              value={anchor} data-testid="upah-anchor-input" onChange={(e) => e.target.value && setAnchor(e.target.value)}
              className={`${inpM} pr-10 font-medium`} />
            <Calendar className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Saldo Upah */}
        <div className="rounded-2xl p-4 text-white shadow-lg shadow-blue-200" style={{ background: GRAD }} data-testid="upah-saldo-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] text-blue-100">Saldo Upah</div>
              <div className="text-[26px] font-bold leading-tight mt-0.5" data-testid="upah-saldo">{formatIDR(data?.saldo || 0)}</div>
              <div className="text-[11px] text-blue-100 mt-1">Upah yang belum dicairkan</div>
              {(data?.total?.ditahan || 0) !== 0 && (
                <div className="text-[10px] text-amber-200 mt-1" data-testid="upah-ditahan-note">Ditahan QC: {formatIDR(data.total.ditahan)} (belum masuk saldo)</div>
              )}
              {(data?.kasbon_sisa || 0) > 0 && (
                <div className="text-[10px] text-blue-200 mt-0.5" data-testid="upah-kasbon-note">Sudah dipotong sisa kasbon: {formatIDR(data.kasbon_sisa)}</div>
              )}
            </div>
            <span className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Wallet className="w-5 h-5" /></span>
          </div>
        </div>

        {/* Ringkasan masuk/keluar periode */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`${cardM} p-3.5 flex items-center gap-2.5`} data-testid="upah-masuk-card">
            <span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><ArrowDownCircle className="w-5 h-5" /></span>
            <div><div className="text-[10px] text-slate-400">Uang Masuk</div><div className="text-sm font-bold text-emerald-600" data-testid="upah-masuk">{formatIDR(data?.period?.masuk || 0)}</div></div>
          </div>
          <div className={`${cardM} p-3.5 flex items-center gap-2.5`} data-testid="upah-keluar-card">
            <span className="w-9 h-9 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center shrink-0"><ArrowUpCircle className="w-5 h-5" /></span>
            <div><div className="text-[10px] text-slate-400">Dicairkan</div><div className="text-sm font-bold text-orange-500" data-testid="upah-keluar">{formatIDR(data?.period?.keluar || 0)}</div></div>
          </div>
        </div>

        {/* Tombol penyesuaian — khusus pengawas (DEMO, nanti pindah ke menu payroll produksi) */}
        {canAdjust && (
          <div className="grid grid-cols-2 gap-3" data-testid="upah-adjust-actions">
            <button data-testid="upah-adjust-masuk-btn" onClick={() => setAdj({ direction: "masuk" })}
              className="h-11 rounded-xl bg-emerald-600 text-white text-xs font-bold shadow-sm active:scale-[0.98] transition-transform">+ Tambah Saldo</button>
            <button data-testid="upah-adjust-keluar-btn" onClick={() => setAdj({ direction: "keluar" })}
              className="h-11 rounded-xl bg-orange-500 text-white text-xs font-bold shadow-sm active:scale-[0.98] transition-transform">− Potong Saldo</button>
            <div className="col-span-2 text-[10px] text-slate-400 text-center -mt-1">Khusus Pengawas • demo — nanti pindah ke menu Payroll Produksi</div>
          </div>
        )}

        {/* Tab arah transaksi */}
        <div className="grid grid-cols-3 gap-2" data-testid="upah-direction-tabs">
          {[["semua", "Semua"], ["masuk", "Masuk"], ["keluar", "Keluar"]].map(([id, label]) => (
            <button key={id} data-testid={`upah-tab-${id}`} onClick={() => setTab(id)}
              className={`h-10 rounded-xl text-xs font-bold transition-colors ${tab === id ? "bg-blue-600 text-white shadow" : "bg-white text-slate-500 border border-slate-200"}`}>{label}</button>
          ))}
        </div>

        {/* Riwayat */}
        <div>
          <h2 className="font-bold text-slate-900 text-sm">Riwayat Transaksi</h2>
          <p className="text-[11px] text-slate-400 mb-2" data-testid="upah-range-label">{upahRangeLabel(mode, anchor, range.from, range.to)}</p>
          {groups.map(([date, list]) => (
            <div key={date} className="mb-3">
              <div className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded-lg px-3 py-1.5 mb-1.5">{formatDateID(date)}</div>
              <div className={`${cardM} divide-y divide-slate-50`}>
                {list.map((t) => (
                  <button key={t.id} data-testid={`upah-tx-${t.id}`} onClick={() => setDetail(t)}
                    className="w-full flex items-center gap-3 px-3.5 py-3 text-left active:bg-slate-50">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      t.direction === "masuk" ? t.badge === "Ditahan" ? "bg-amber-50 text-amber-500" : "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-500"}`}>
                      {t.direction === "masuk" ? <ArrowDownCircle className="w-5 h-5" /> : <ArrowUpCircle className="w-5 h-5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-slate-800 truncate">{t.title}</div>
                      <div className="text-[10px] text-slate-400 truncate">{t.subtitle}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-xs font-bold ${t.direction === "masuk" ? "text-emerald-600" : "text-orange-500"}`}>
                        {t.direction === "masuk" ? "+" : "−"}{formatIDR(Math.abs(t.amount))}
                      </div>
                      <div className="text-[9px] text-slate-400">{t.badge}</div>
                      {t.kind === "pencairan" && <div className="text-[8px] text-sky-600 font-semibold" data-testid={`upah-tx-cair-${t.id}`}>Saldo sudah dicairkan</div>}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!groups.length && <div className={`${cardM} py-10 text-center text-xs text-slate-400`} data-testid="upah-empty">Belum ada transaksi pada periode ini.</div>}
          {groups.length > 0 && <div className="text-center text-[10px] text-slate-400 mt-1" data-testid="upah-count">Menampilkan {txs.length} transaksi</div>}
        </div>
      </div>

      {/* Modal detail transaksi */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setDetail(null)} data-testid="upah-detail-overlay">
          <div className="bg-white w-full max-w-md rounded-t-3xl p-5 pb-8" onClick={(e) => e.stopPropagation()} data-testid="upah-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 text-sm">Detail Transaksi</h3>
              <button data-testid="upah-detail-close" onClick={() => setDetail(null)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <div className={`text-center py-4 rounded-2xl mb-4 ${detail.direction === "masuk" ? "bg-emerald-50" : "bg-orange-50"}`}>
              <div className={`text-2xl font-bold ${detail.direction === "masuk" ? "text-emerald-600" : "text-orange-500"}`}>
                {detail.direction === "masuk" ? "+" : "−"}{formatIDR(Math.abs(detail.amount))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{detail.title}</div>
            </div>
            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">Tanggal</span><span className="font-semibold text-slate-700">{detail.date ? formatDateID(detail.date) : "-"}</span></div>
              {detail.ref_number && <div className="flex justify-between"><span className="text-slate-400">Referensi</span><span className="font-semibold text-slate-700">{detail.ref_number}</span></div>}
              <div className="flex justify-between"><span className="text-slate-400">Status</span><span className="font-semibold text-slate-700 capitalize">{detail.badge}</span></div>
              {detail.kind === "pencairan" && (
                <div className="flex justify-between items-center rounded-lg bg-sky-50 border border-sky-100 px-2.5 py-1.5" data-testid="upah-detail-cair">
                  <span className="text-sky-700 font-semibold text-[11px]">Saldo sudah dicairkan</span>
                  {detail.cairkan_at && <span className="text-sky-500 text-[10px]">{formatDateID(detail.cairkan_at.slice(0, 10))}</span>}
                </div>
              )}
              {detail.cairkan_by && <div className="flex justify-between"><span className="text-slate-400">Dicairkan oleh</span><span className="font-semibold text-slate-700">{detail.cairkan_by}</span></div>}
              {detail.note && <div className="pt-2 border-t border-slate-100"><span className="text-slate-400">Catatan</span><p className="text-slate-600 mt-0.5 leading-relaxed">{detail.note}</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Modal penyesuaian saldo (pengawas) */}
      {adj && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => !busy && setAdj(null)} data-testid="upah-adjust-overlay">
          <div className="bg-white w-full max-w-md rounded-t-3xl p-5 pb-8" onClick={(e) => e.stopPropagation()} data-testid="upah-adjust-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 text-sm">{adj.direction === "masuk" ? "Tambah Saldo Karyawan" : "Potong Saldo Karyawan"}</h3>
              <button data-testid="upah-adjust-close" onClick={() => !busy && setAdj(null)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Karyawan</label>
                <select data-testid="upah-adjust-employee" value={adjForm.employee_id} onChange={(e) => setAdjForm({ ...adjForm, employee_id: e.target.value })} className={inpM}>
                  <option value="">— Pilih karyawan —</option>
                  {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Nominal (Rp)</label>
                <input data-testid="upah-adjust-amount" inputMode="numeric" placeholder="cth: 100000" value={adjForm.amount}
                  onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value.replace(/\D/g, "") })} className={inpM} />
              </div>
              {adj.direction === "keluar" && (
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Metode</label>
                  <select data-testid="upah-adjust-method" value={adjForm.method} onChange={(e) => setAdjForm({ ...adjForm, method: e.target.value })} className={inpM}>
                    <option>Transfer BCA</option><option>Transfer Mandiri</option><option>Transfer BRI</option><option>Tunai</option><option>E-Wallet</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Catatan</label>
                <input data-testid="upah-adjust-note" placeholder={adj.direction === "masuk" ? "cth: Bonus target mingguan" : "cth: Potongan kasbon / denda"} value={adjForm.note}
                  onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })} className={inpM} />
              </div>
              {adj.direction === "keluar" && adjForm.employee_id === user?.id && (
                <p className="text-[10px] text-amber-600 bg-amber-50 rounded-lg px-3 py-2">Saldo Anda langsung berkurang dan transaksi tercatat sebagai Keluar (boleh minus).</p>
              )}
              <button data-testid="upah-adjust-submit" disabled={busy} onClick={submitAdj}
                className={`w-full h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 ${adj.direction === "masuk" ? "bg-emerald-600" : "bg-orange-500"} disabled:opacity-50`}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QcPickScreen({ pending, employees, onBack, onPick }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const empIds = new Set(employees.filter((e) => (e.name + (e.employee_id || "")).toLowerCase().includes(query)).map((e) => e.id));
  const list = pending.filter((b) => !query
    || empIds.has(b.sewer_id) || (b.sewer_name || "").toLowerCase().includes(query)
    || (b.number || "").toLowerCase().includes(query) || (b.spk_number || "").toLowerCase().includes(query)
    || (b.product_name || "").toLowerCase().includes(query));
  return (
    <div data-testid="screen-qc-pick">
      <SubHeader title="Input Hasil QC" onBack={onBack} testid="qcpick-header" />
      <div className="px-4 pb-4 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="qc-pick-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / ID penjahit, kode bundel, no SPK, atau produk" className={`${inpM} pl-10`} />
        </div>
        <p className="text-[11px] text-slate-400 font-semibold">Antrean Pemeriksaan ({list.length})</p>
        <div className="space-y-2.5">
          {list.map((b) => (
            <button key={b.id} data-testid={`qc-pick-item-${b.id}`} onClick={() => onPick(b.id)} className={`${cardM} w-full p-3.5 text-left`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-semibold whitespace-nowrap">Menunggu QC • {b.qty} pcs</span>
              </div>
              <div className="text-xs font-semibold text-slate-700 mt-1 truncate">{b.product_name} • {b.variant}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{b.spk_number} • Penjahit: {b.sewer_name}</div>
            </button>
          ))}
          {!list.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="qc-pick-empty">{pending.length ? "Tidak ada bundel cocok dengan pencarian." : "Tidak ada bundel menunggu pemeriksaan."}</div>}
        </div>
      </div>
    </div>
  );
}

function QcFormScreen({ bundle, onBack, onDone, onStale }) {
  const [f, setF] = useState({ physical: String(bundle.qty), passed: "", repair: "", reject: "", repair_reason: "", reject_reason: "", reject_photo: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const p = (k) => parseInt(f[k]) || 0;
  const sum = p("passed") + p("repair") + p("reject");
  const valid = sum === p("physical") && p("physical") >= 0;
  const upload = async (e) => {
    const fl = e.target.files?.[0]; e.target.value = "";
    if (!fl) return;
    try { const fd = new FormData(); fd.append("file", fl); const { data } = await api.post("/uploads", fd); setF((s) => ({ ...s, reject_photo: data.url })); toast.success("Foto terunggah"); }
    catch { toast.error("Gagal mengunggah foto"); }
  };
  const submit = async () => {
    if (!valid) return toast.error("Lolos + perbaikan + reject harus sama dengan jumlah fisik");
    if (p("repair") > 0 && !f.repair_reason.trim()) return toast.error("Alasan perbaikan wajib diisi");
    if (p("reject") > 0 && !f.reject_reason.trim()) return toast.error("Alasan reject wajib diisi");
    setBusy(true);
    try {
      await api.post("/prod/qc/inspect", {
        bundle_id: bundle.id, physical_qty: p("physical"), passed: p("passed"), repair: p("repair"), reject: p("reject"),
        repair_destination: p("repair") > 0 ? `Kembali ke penjahit: ${bundle.sewer_name}` : "",
        repair_reason: f.repair_reason, reject_reason: f.reject_reason, reject_photo: f.reject_photo || null,
      });
      onDone({ number: bundle.number, passed: p("passed"), repair: p("repair"), reject: p("reject") });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan pemeriksaan";
      if (String(msg).includes("status menunggu")) {
        toast.error("Bundel ini baru saja diperiksa di sesi lain — daftar diperbarui.");
        onStale?.();
      } else toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="screen-qc-form">
      <SubHeader title="Periksa Bundel" onBack={onBack} testid="qcform-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <div className={`${cardM} p-3.5`} data-testid="qcform-bundle-card">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs font-bold text-slate-800">{bundle.number}</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-semibold">Tercatat {bundle.qty} pcs</span>
          </div>
          <div className="text-xs font-semibold text-slate-700 mt-1">{bundle.product_name} • {bundle.variant}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{bundle.spk_number} • Penjahit: {bundle.sewer_name}</div>
        </div>
        {p("physical") !== bundle.qty && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5" data-testid="qcm-selisih-note">Fisik berbeda dari tercatat — selisih otomatis diajukan ke pengawas.</div>}
        <div className={`${cardM} p-4 space-y-3`}>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold text-slate-500">Jumlah Fisik</label><input data-testid="qcm-physical" type="number" min="0" value={f.physical} onChange={(e) => setF({ ...f, physical: e.target.value })} className={inpM} /></div>
            <div><label className="text-[11px] font-semibold text-emerald-600">Lolos</label><input data-testid="qcm-passed" type="number" min="0" value={f.passed} onChange={(e) => setF({ ...f, passed: e.target.value })} className={inpM} /></div>
            <div><label className="text-[11px] font-semibold text-amber-600">Perlu Perbaikan</label><input data-testid="qcm-repair" type="number" min="0" value={f.repair} onChange={(e) => setF({ ...f, repair: e.target.value })} className={inpM} /></div>
            <div><label className="text-[11px] font-semibold text-red-500">Reject Permanen</label><input data-testid="qcm-reject" type="number" min="0" value={f.reject} onChange={(e) => setF({ ...f, reject: e.target.value })} className={inpM} /></div>
          </div>
          <div data-testid="qcm-sum" className={`text-xs font-bold text-center rounded-xl py-2 ${valid ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
            {p("passed")} + {p("repair")} + {p("reject")} = {sum} {valid ? "= fisik ✓" : `≠ fisik ${p("physical")}`}
          </div>
          {p("repair") > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">Perbaikan otomatis kembali ke penjahit yang sama: <b>{bundle.sewer_name}</b>. Saldo penjahit untuk bagian ini ditahan dulu.</div>
              <input data-testid="qcm-repair-reason" value={f.repair_reason} onChange={(e) => setF({ ...f, repair_reason: e.target.value })} placeholder="Alasan perbaikan *" className={inpM} />
            </div>
          )}
          {p("reject") > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">Reject permanen: pengawas yang menentukan besar potongan.</div>
              <input data-testid="qcm-reject-reason" value={f.reject_reason} onChange={(e) => setF({ ...f, reject_reason: e.target.value })} placeholder="Alasan reject *" className={inpM} />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
              <button data-testid="qcm-reject-photo" onClick={() => fileRef.current?.click()} className="w-full h-10 px-3 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500 flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> {f.reject_photo ? "Foto terlampir" : "Unggah foto bukti *"}</button>
            </div>
          )}
        </div>
        <button data-testid="qcm-submit" onClick={submit} disabled={busy || !valid}
          className="w-full h-12 rounded-2xl text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-blue-200" style={{ background: BLUE }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Simpan Hasil QC
        </button>
      </div>
    </div>
  );
}

function BundleList({ bundles, spkImg, badgeFor, onPick, empty }) {
  return (
    <div className="px-4 mt-3.5 space-y-2.5 pb-4">
      {bundles.map((b) => (
        <button key={b.id} data-testid={`bundle-card-item-${b.id}`} onClick={() => onPick(b.id)} className="w-full text-left relative">
          <BundleCard b={b} img={spkImg[b.spk_id]} badgeFor={badgeFor} />
          <ChevronRight className="w-4 h-4 text-slate-300 absolute right-3.5 top-4" />
        </button>
      ))}
      {!bundles.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`} data-testid="bundle-empty">{empty}</div>}
    </div>
  );
}
